import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import PdfTileLayer from './PdfTileLayer'
import DxfLayer from './DxfLayer'
import { detectDxfUnit, buildDxfCalibration } from '../../lib/dxfUnitDetection'
import { colors } from '../../styles/tokens'

// PDF.js worker setup now lives inside PdfTileLayer.jsx, since that is
// the only place pdfjs-dist is used directly in this module.

// =======================================================================
// CANONICAL COORDINATE SYSTEM (Sprint 1 — validated, unchanged here)
// =======================================================================
// pdf coordinates -> world/document coordinates -> calibration -> screen
// screenPoint = pdfPoint * zoom + pan
// pdfPoint = (screenPoint - pan) / zoom
// Zoom/pan are a pure VIEW transform. Geometry is always stored and
// computed in PDF space. See src/lib/geometryEngine.test.js for the
// permanent automated proof of zoom/pan invariance.
// =======================================================================

const PAGE_BASE_WIDTH = 900
const PAGE_BASE_HEIGHT = PAGE_BASE_WIDTH * 1.414

// Resolution-aware rendering: PDF.js re-renders the page raster at this
// scale (independent of the CSS view-transform "zoom" used for pan/live
// interaction). This is the actual fix for blur at high zoom — the
// previous implementation rendered once at a fixed 900px raster and then
// stretched it purely with CSS transform, which is what caused the loss
// of sharpness. Debounced so a rapid mouse-wheel zoom does not trigger a
// full PDF.js re-render on every intermediate frame.
const MAX_ZOOM = 20 // 2000%
const MIN_ZOOM = 0.1

// Whole-page render scaling (computeRenderScale) is no longer needed —
// PdfTileLayer now handles per-tile resolution based on the visible
// viewport, which is the actual fix that allows sharp rendering up to
// 2000% zoom without a single giant full-page raster.

const TOOLS = [
  { id: 'select', label: 'Select', icon: '↖', shortcut: 'V' },
  { id: 'window-select', label: 'Window Select', icon: '▯', shortcut: 'W' },
  { id: 'crossing-select', label: 'Crossing Select', icon: '▨', shortcut: 'X' },
  { id: 'lasso-select', label: 'Lasso Select', icon: '➰', shortcut: 'G' },
  { id: 'pan', label: 'Pan', icon: '✋', shortcut: 'H' },
  { id: 'zoom-area', label: 'Zoom Area', icon: '🔍', shortcut: '' }, // no keyboard shortcut, per approved scope — Toolbar-only activation.
  { id: 'calibrate', label: 'Calibrate', icon: '📏', shortcut: 'C' },
  { id: 'length', label: 'Length', icon: '／', shortcut: 'L' },
  { id: 'polyline', label: 'Polyline', icon: '⌇', shortcut: 'P' },
  { id: 'area', label: 'Area', icon: '⬠', shortcut: 'A' },
  { id: 'rectangle', label: 'Rectangle', icon: '▭', shortcut: 'R' },
  { id: 'count', label: 'Count', icon: '●', shortcut: 'N' },
  { id: 'dimension', label: 'Dimension', icon: '↔', shortcut: 'D' },
]

const SNAP_RADIUS_PX = 10

export default function TakeoffCanvas({
  fileUrl,
  pageIndex,
  fileType = 'pdf', // Package 9-10 — 'pdf' | 'dxf'
  dxfUnitCode = null,
  dxfUnitSource = null,
  onDxfUnitDetected, // (unitCode) => void — reports the auto-detected code up to TakeoffPage so it can be persisted
  onDxfUnitRequired, // () => void — signals that the unit could not be auto-detected and manual confirmation is needed
  calibration,
  onCalibrate,
  onMeasurementComplete,
  expectedMeasurementType,
  existingMeasurements: allExistingMeasurements = [],
  onSelectMeasurement,
  onMarqueeSelect, // (matchedIds: string[], { additive: boolean }) => void — PHASE 3
  onDuplicateMeasurement, // (sheetId, takeoffItemId, calibrationId, measurementType, geometryData, calculatedValue) => void — Package 1c
  onTogglePolarity, // (measurementId, newPolarity) => void — Package 2
  onSubmitForReview, // (measurementIds: string[]) => void — Package 4
  lockedIds = new Set(), // Package 3 — locked measurement ids, checked at every mutation entry point below.
  isolateActive = false, // Package 3 — Isolate overlay flag (visibility only, computed here, never mutating hiddenIds/hiddenLayers).
  setIsolateActive, // (fn) => void — toggles the overlay flag; owned by TakeoffPage.
  hiddenLayers = new Set(),
  setLockedIds, // Package 3
  opacityMap = new Map(), // Package 3 — measurementId -> opacity (0.1–1.0). Purely visual.
  setOpacityMap,
  rightCollapsed, rightSidebarCollapsed, setRightSidebarCollapsed, // Package 8 (rightSidebarCollapsed/setRightSidebarCollapsed unused directly here but kept for prop-completeness; Full Canvas only needs rightCollapsed + the toggle below)
  fullCanvasMode = false,
  setFullCanvasMode,
  selectedMeasurementId,
  selectedMeasurementIds = [],
  onGeometryEdited, // (measurementId, newGeometryData, newCalculatedValue) => void
  hiddenIds = new Set(),
  showGrid = true,
  gridSpacing = 50, // in PDF units
  snapEnabled = true,
  testMode = false,
}) {
  // Only measurements the canvas can actually draw and select. A measurement
  // whose stored geometry is incomplete (for example one entered by hand with
  // just a note and a type, and no points) has nothing to render, and used to
  // crash the whole Takeoff page ("Cannot read properties of undefined
  // (reading 'map')"). It still appears in the measurements list and still
  // counts toward its quantity; it is simply not drawn or hit-tested here.
  const existingMeasurements = useMemo(() => allExistingMeasurements.filter(hasDrawableGeometry), [allExistingMeasurements])
  const [tool, setTool] = useState('select')
  const [points, setPoints] = useState([])
  const [zoom, setZoom] = useState(1)
  // Zoom Area — declared early (before any useEffect that references
  // it in a dependency array) to avoid a temporal-dead-zone
  // ReferenceError. Mirrors marqueeStart/marqueeCurrent's own state
  // shape and lifecycle.
  const [zoomAreaStart, setZoomAreaStart] = useState(null)
  const [zoomAreaCurrent, setZoomAreaCurrent] = useState(null)
  const zoomAreaStartClientRef = useRef(null)

  const [pan, setPan] = useState({ x: 40, y: 20 })
  const [isPanning, setIsPanning] = useState(false)
  const [cursorPdf, setCursorPdf] = useState(null)
  const [livePreview, setLivePreview] = useState(null)
  // Hover/pre-selection (P1). Purely visual client-side state — never
  // written anywhere, never affects geometry/quantity/BOQ.
  const [hoveredId, setHoveredId] = useState(null)
  const [history, setHistory] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const [spaceHeld, setSpaceHeld] = useState(false)
  const [pdfError, setPdfError] = useState(null)
  const [snapIndicator, setSnapIndicator] = useState(null)
  const [draggingVertex, setDraggingVertex] = useState(null) // { measurementId, vertexIndex, geometryType }
  // Package 1b UX revision — explicit vertex selection, replacing
  // double-click entirely. { measurementId, vertexIndex } | null.
  // Right-click on a vertex both selects it AND opens the minimal
  // context menu; Delete/Backspace acts on whichever vertex is
  // currently selected this way.
  const [selectedVertex, setSelectedVertex] = useState(null)
  // Direct Drag-to-Move — body drag state. bodyDragRef holds mutable
  // drag-in-progress data (screen-space start point + the PDF-space
  // ids), read/written synchronously during mousemove (never via
  // React state, to avoid re-render overhead on every pixel of
  // movement — the same performance pattern already used for
  // vertex-drag's editedGeometry live preview). liveOffset is the only
  // piece that needs to trigger a re-render (to show the shape moving
  // live), so it alone is React state.
  const bodyDragRef = useRef(null) // { measurementId, startClientX, startClientY, startZoom, thresholdCrossed }
  const [bodyDragLiveOffset, setBodyDragLiveOffset] = useState(null) // { measurementId, dx, dy } in PDF-space units, or null
  const DRAG_THRESHOLD_PX = 4 // screen pixels — click vs drag distinction, independent of zoom.

  // Package 3 — unified visibility predicate. Isolate is a pure OVERLAY:
  // when active, only the currently-selected measurement (or any member
  // of a multi-selection, if present) is visible — hiddenIds/hiddenLayers
  // are read but never mutated, so Exit Isolate needs zero restoration
  // logic; the underlying state was never touched. All three existing
  // call sites (marquee, lasso, and the main render loop) now go
  // through this single function instead of repeating the condition.
  function isVisible(m) {
    if (isolateActive) {
      const isSelected = m.id === selectedMeasurementId || selectedMeasurementIds.includes(m.id)
      return isSelected && !hiddenIds.has(m.id) && !hiddenLayers.has(m.measurement_type)
    }
    return !hiddenIds.has(m.id) && !hiddenLayers.has(m.measurement_type)
  }

  // Package 3 — Isolate toggle. A pure overlay flag: entering Isolate
  // never touches hiddenIds/hiddenLayers, so exiting requires zero
  // restoration logic — the underlying state was never mutated.
  function toggleIsolate() {
    setIsolateActive?.((prev) => !prev)
  }

  // Package 3 — Lock toggle. Local UI state (Set of ids) owned by
  // TakeoffPage, exactly mirroring the existing hiddenIds pattern —
  // no new architecture, no database column, no geometry history entry.
  function toggleLock(measurementId) {
    setLockedIds?.((prev) => {
      const next = new Set(prev)
      next.has(measurementId) ? next.delete(measurementId) : next.add(measurementId)
      return next
    })
  }

  const [vertexContextMenu, setVertexContextMenu] = useState(null) // { x, y, measurementId, vertexIndex } screen-space, for the popup position
  // Guards against finishVertexDrag() firing twice for the SAME drag
  // gesture — onMouseUp and onMouseLeave both call handleMouseUp, and
  // the mouse can leave the Canvas bounds momentarily during a drag
  // before the button is released, firing both handlers in quick
  // succession. A ref is used (not state) because it updates
  // synchronously — setDraggingVertex(null) alone is not fast enough
  // to prevent a second call arriving before React re-renders.
  const finishingDragRef = useRef(false)
  // Independent, synchronous guards for Undo/Redo — deliberately SEPARATE
  // refs (not one shared flag) so a legitimate Undo does not block a
  // subsequent legitimate Redo, or vice versa. State setters
  // (setGeometryEditHistory/setGeometryRedoStack) are asynchronous;
  // reading .length from React state inside the function body reflects
  // the value from the LAST completed render, not the current call —
  // two rapid Ctrl+Z presses can therefore both read the same
  // "length > 0" and the same top-of-stack entry before either state
  // update commits. A ref is read/written synchronously and is
  // therefore immune to this timing window.
  const undoInProgressRef = useRef(false)
  // Package 1b fix: true while ANY commitGeometryEdit (Add/Remove
  // Vertex, Move, or a normal vertex drag) is awaiting its Supersede
  // round-trip. Vertex/mid-edge handles are hidden during this window
  // so the user cannot double-click a handle whose underlying
  // measurement id is about to become stale — closing the exact race
  // that caused "is not active" on rapid consecutive edits.
  const [editInFlight, setEditInFlight] = useState(false)
  const redoInProgressRef = useRef(false)
  const [editedGeometry, setEditedGeometry] = useState(null) // live preview of geometry being edited // { point, type: 'endpoint'|'midpoint'|'intersection'|'grid' }
  // Undo/Redo for EXISTING-geometry edits (P0). Deliberately a SEPARATE
  // stack from `history`/`redoStack` above, which remain exclusively
  // for in-progress new-shape drawing — that separation was the
  // recommended architecture (Option A) because a persisted-measurement
  // edit is a fundamentally different operation (goes through
  // Supersede immediately) from an unsaved draft polyline. One entry =
  // one completed drag gesture (recorded once, in finishVertexDrag —
  // never per mousemove), matching the state model finalized in the
  // prior audit.
  const [geometryEditHistory, setGeometryEditHistory] = useState([])
  const [geometryRedoStack, setGeometryRedoStack] = useState([])
  const [gridOn, setGridOn] = useState(showGrid)
  const [snapOn, setSnapOn] = useState(snapEnabled)

  const panStart = useRef(null)
  const containerRef = useRef(null)

  // Package 3+8 — ResizeObserver. Updates ONLY a state var tracking
  // the container's current dimensions — it does NOT call fitWidth/
  // fitHeight/fitDrawing automatically, and does NOT touch zoom/pan.
  // Per the approved architecture, Fit remains an explicit user
  // action; this observer exists solely so future code (or the user's
  // own explicit Fit click) always reads the CURRENT viewport size
  // rather than a stale one from before a sidebar/Full-Canvas toggle.
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })

  // Package 9-10 — DXF loading via Web Worker. Parsing never touches
  // the main thread; this component only receives the already-
  // normalized entity list back and renders it via DxfLayer.
  const [dxfEntities, setDxfEntities] = useState([])
  const [dxfUnsupportedCounts, setDxfUnsupportedCounts] = useState(null)
  const [dxfLoadError, setDxfLoadError] = useState(null)
  // Fit-to-View fix — one-time-per-load guard. Keyed to fileUrl so a
  // genuinely NEW DXF triggers exactly one auto-fit, while user
  // zoom/pan afterward (or a ResizeObserver firing from a sidebar
  // toggle) never re-triggers it. Reset to false whenever fileUrl
  // changes (a new drawing was loaded).
  const dxfFitDoneRef = useRef(null) // holds the fileUrl the fit was already performed for
  useEffect(() => {
    if (fileType !== 'dxf' || !fileUrl) { setDxfEntities([]); return }
    let cancelled = false
    setDxfLoadError(null)
    const worker = new Worker(new URL('../../lib/dxfParser.worker.js', import.meta.url), { type: 'module' })
    worker.onmessage = (e) => {
      if (cancelled) return
      const result = e.data
      if (!result.ok) { setDxfLoadError(result.error); worker.terminate(); return }
      setDxfEntities(result.entities)
      setDxfUnsupportedCounts({ total: result.totalCount, rendered: result.renderedCount, unsupported: result.unsupportedCounts })
      // Report the detected unit UP to TakeoffPage — this component
      // never writes to the database itself; it only reports findings.
      const detection = detectDxfUnit({ header: { $INSUNITS: result.unitCode } })
      if (detection.status === 'detected') onDxfUnitDetected?.(detection.code)
      else onDxfUnitRequired?.()
      // First-load Fit-to-View — computed here, once, directly from
      // the entities this Worker message just delivered (never from
      // React state, which would still be stale in this same tick).
      // Reuses the EXISTING zoom/pan state via setZoom/setPan — no
      // second viewport engine. Only runs if this exact fileUrl has
      // not already been auto-fitted (guards against ResizeObserver,
      // Fit Width/Height clicks, or any other re-render from
      // re-triggering it).
      if (dxfFitDoneRef.current !== fileUrl && containerRef.current) {
        const bbox = computeDxfBoundingBox(result.entities)
        if (bbox) {
          const w = containerRef.current.clientWidth
          const h = containerRef.current.clientHeight
          const bboxWidth = bbox.maxX - bbox.minX || 1
          const bboxHeight = bbox.maxY - bbox.minY || 1
          const zw = (w - 60) / bboxWidth
          const zh = (h - 60) / bboxHeight
          const newZoom = Math.min(zw, zh)
          const centerX = (bbox.minX + bbox.maxX) / 2
          const centerY = (bbox.minY + bbox.maxY) / 2
          // DXF entities render at their raw coordinates inside the
          // same PAGE_BASE_WIDTH/HEIGHT-sized SVG the PDF stack uses
          // (see the render block below) — so panning must place the
          // bbox CENTER at the middle of that same page-space canvas,
          // exactly mirroring how fitDrawing() centers PDF content.
          setZoom(newZoom)
          setPan({ x: w / 2 - centerX * newZoom, y: h / 2 - centerY * newZoom })
        }
        dxfFitDoneRef.current = fileUrl
      }
      worker.terminate()
    }
    fetch(fileUrl).then((r) => r.text()).then((text) => { if (!cancelled) worker.postMessage({ fileText: text }) })
    return () => { cancelled = true; worker.terminate() }
  }, [fileType, fileUrl])
  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setViewportSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  // ARCHITECTURAL FIX: a single ref holding the LATEST values needed by
  // the selection pipeline (existingMeasurements, zoom, pan). Selection
  // handlers read from this ref, never from closed-over render-scope
  // variables — this eliminates any race window where a click event
  // fires against a stale snapshot from a previous render (the root
  // architectural weakness behind "first click sometimes does nothing/
  // picks the wrong candidate, subsequent clicks eventually work").
  // Updated synchronously on every render — no extra state, no timers.
  const latestRef = useRef({ existingMeasurements, zoom, pan, hiddenIds, hiddenLayers, onMarqueeSelect })
  latestRef.current.existingMeasurements = existingMeasurements
  latestRef.current.zoom = zoom
  latestRef.current.pan = pan
  latestRef.current.hiddenIds = hiddenIds
  latestRef.current.hiddenLayers = hiddenLayers
  latestRef.current.onMarqueeSelect = onMarqueeSelect

  const scaleFactor = calibration ? calibration.real_distance / calibration.pdf_distance : null

  const screenToPdf = useCallback((clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect()
    const { zoom: z, pan: p } = latestRef.current
    return { x: (clientX - rect.left - p.x) / z, y: (clientY - rect.top - p.y) / z }
  }, [])

  // The visible viewport rectangle, in PDF/world coordinates, computed
  // via the exact same screenToPdf transform used everywhere else in
  // this component (clicks, snap, geometry). PdfTileLayer uses this only
  // to decide which tiles to render — it performs no coordinate math of
  // its own.
  const [viewportRect, setViewportRect] = useState(null)
  useEffect(() => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const topLeft = screenToPdf(rect.left, rect.top)
    const bottomRight = screenToPdf(rect.left + rect.width, rect.top + rect.height)
    setViewportRect({
      x: topLeft.x, y: topLeft.y,
      width: bottomRight.x - topLeft.x, height: bottomRight.y - topLeft.y,
    })
  }, [pan, zoom, screenToPdf])

  // -----------------------------------------------------------------
  // SNAP ENGINE — endpoint, midpoint, vertex (same as endpoint for our
  // polyline/polygon data), intersection, and grid. All computed in PDF
  // space so results are zoom/pan invariant like everything else here.
  // -----------------------------------------------------------------
  function allSegments() {
    const segments = []
    for (const m of existingMeasurements) {
      const pts = extractPoints(m.geometry_data)
      const isClosed = m.geometry_data?.type === 'polygon'
      for (let i = 0; i < pts.length - 1; i++) segments.push([pts[i], pts[i + 1]])
      if (isClosed && pts.length > 2) segments.push([pts[pts.length - 1], pts[0]])
      if (m.geometry_data?.type === 'rectangle' && pts.length === 2) {
        const [a, b] = pts
        const c = { x: b.x, y: a.y }, d = { x: a.x, y: b.y }
        segments.push([a, c], [c, b], [b, d], [d, a])
      }
    }
    // also include the in-progress polyline/polygon being drawn
    for (let i = 0; i < points.length - 1; i++) segments.push([points[i], points[i + 1]])
    return segments
  }

  function lineIntersection(p1, p2, p3, p4) {
    const d = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x)
    if (Math.abs(d) < 1e-9) return null
    const t = ((p3.x - p1.x) * (p4.y - p3.y) - (p3.y - p1.y) * (p4.x - p3.x)) / d
    const u = ((p3.x - p1.x) * (p2.y - p1.y) - (p3.y - p1.y) * (p2.x - p1.x)) / d
    if (t < 0 || t > 1 || u < 0 || u > 1) return null
    return { x: p1.x + t * (p2.x - p1.x), y: p1.y + t * (p2.y - p1.y) }
  }

  function nearestGridPoint(pt) {
    return { x: Math.round(pt.x / gridSpacing) * gridSpacing, y: Math.round(pt.y / gridSpacing) * gridSpacing }
  }

  function snapPdfPoint(pt) {
    if (!snapOn) { setSnapIndicator(null); return { ...pt, snapped: false } }
    const snapRadiusPdf = SNAP_RADIUS_PX / zoom

    // 1. Endpoint/vertex snap (highest priority)
    const endpoints = [...points, ...existingMeasurements.flatMap((m) => extractPoints(m.geometry_data))]
    for (const c of endpoints) {
      if (Math.hypot(c.x - pt.x, c.y - pt.y) <= snapRadiusPdf) {
        setSnapIndicator({ point: c, type: 'endpoint' })
        return { x: c.x, y: c.y, snapped: true }
      }
    }

    // Performance: pre-filter to only segments whose bounding box is near
    // the cursor before doing any midpoint/intersection math. With large
    // Golden-Project-scale measurement counts (100+), this avoids the
    // O(n^2) intersection scan running on every single mouse-move event
    // over segments that are nowhere near the pointer.
    const searchPad = snapRadiusPdf * 3
    const nearbySegments = allSegments().filter(([a, b]) => {
      const minX = Math.min(a.x, b.x) - searchPad, maxX = Math.max(a.x, b.x) + searchPad
      const minY = Math.min(a.y, b.y) - searchPad, maxY = Math.max(a.y, b.y) + searchPad
      return pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY
    })

    // 2. Midpoint snap
    for (const [a, b] of nearbySegments) {
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      if (Math.hypot(mid.x - pt.x, mid.y - pt.y) <= snapRadiusPdf) {
        setSnapIndicator({ point: mid, type: 'midpoint' })
        return { x: mid.x, y: mid.y, snapped: true }
      }
    }

    // 3. Intersection snap — only among the already-filtered nearby segments
    for (let i = 0; i < nearbySegments.length; i++) {
      for (let j = i + 1; j < nearbySegments.length; j++) {
        const inter = lineIntersection(nearbySegments[i][0], nearbySegments[i][1], nearbySegments[j][0], nearbySegments[j][1])
        if (inter && Math.hypot(inter.x - pt.x, inter.y - pt.y) <= snapRadiusPdf) {
          setSnapIndicator({ point: inter, type: 'intersection' })
          return { x: inter.x, y: inter.y, snapped: true }
        }
      }
    }

    // 4. Grid snap (lowest priority, only if grid is shown)
    if (gridOn) {
      const gridPt = nearestGridPoint(pt)
      if (Math.hypot(gridPt.x - pt.x, gridPt.y - pt.y) <= snapRadiusPdf) {
        setSnapIndicator({ point: gridPt, type: 'grid' })
        return { x: gridPt.x, y: gridPt.y, snapped: true }
      }
    }

    setSnapIndicator(null)
    return { ...pt, snapped: false }
  }
  function extractPoints(g) {
    if (!g) return []
    if (g.points) return g.points
    if (g.point) return [g.point]
    return []
  }

  useEffect(() => {
    function handleKeyDown(e) {
      // TEMPORARY DIAGNOSTIC (Log 1) — unconditional, first statement.
      if (e.code === 'Space') { setSpaceHeld(true); e.preventDefault() }
      if (e.key === 'Escape') {
        // Zoom Area cancellation takes priority over everything else —
        // higher priority than the normal drawing-point Escape
        // handling. Outside an active Zoom Area drag, all existing
        // Escape behavior below is completely unchanged.
        if (zoomAreaStart) {
          setZoomAreaStart(null)
          setZoomAreaCurrent(null)
          zoomAreaStartClientRef.current = null
          return
        }
        // Documented interaction order: an in-progress drawing takes
        // priority (cancelling it protects against losing in-progress
        // points — the pre-existing, safer behavior). Only when there
        // is NO active drawing does Escape clear the current
        // selection. If both happen to be true, the next Escape press
        // clears the selection.
        if (points.length > 0) cancelDrawing()
        else { onSelectMeasurement?.(null); setSelectedVertex(null); setVertexContextMenu(null) }
      }
      if ((e.key === 'Enter') && (tool === 'polyline' || tool === 'area' || tool === 'dimension') && points.length >= (tool === 'area' ? 3 : 2)) {
        finishMultiPoint()
      }
      // Package 1b — Delete/Backspace removes the explicitly
      // right-click-selected vertex, taking priority over the
      // existing in-progress-drawing undo ONLY when there is no
      // in-progress drawing (points.length === 0) — this preserves
      // the pre-existing Backspace behavior exactly when a shape is
      // actively being drawn.
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedVertex && points.length === 0 && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault()
        const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedVertex.measurementId)
        if (m) removeVertex(m, selectedVertex.vertexIndex)
      } else if (e.key === 'Backspace' && points.length > 0 && document.activeElement?.tagName !== 'INPUT') { handleUndo() }
      // Package 1b — Move Complete Geometry via arrow keys on the
      // currently selected measurement. Step is in PDF-space units
      // (independent of zoom, matching how all other geometry math in
      // this file already works), Shift for a larger nudge — this
      // exercises the full commitGeometryEdit -> Supersede -> Undo/Redo
      // path without introducing a new drag-surface architecture.
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && tool === 'select' && selectedMeasurementId && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault()
        const step = e.shiftKey ? 10 : 1
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
        const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId)
        if (m) moveGeometry(m, dx, dy)
      }
      // Package 1c — Rotation, ±90°, on the currently selected
      // P0: Ctrl/Cmd+Z checks geometry-edit history FIRST (more likely
      // to reflect the user's most recent intent — an edit to an
      // existing measurement), falling back to the pre-existing
      // in-progress-drawing history exactly as before. These are two
      // genuinely separate stacks (see the architecture note above);
      // this ordering is the only new decision, and it never mixes
      // the two histories into one entry.
      if (e.key.toLowerCase() === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        if (geometryEditHistory.length > 0) undoGeometryEdit()
        else handleUndo()
      }
      if (e.key.toLowerCase() === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault()
        if (geometryRedoStack.length > 0) redoGeometryEdit()
        else handleRedo()
      }
      // PHASE 4 — Select All. Only when Select tool is active, so
      // Ctrl+A doesn't fire while e.g. drawing an Area/Rectangle. Reads
      // from latestRef, never from render-scope closures, to avoid the
      // same stale-closure class of bug fixed earlier for selection.
      // FIX: preventDefault() alone was not reliably stopping the
      // browser's native "select all text" behavior in every case —
      // also stopping propagation and checking this FIRST (before any
      // other key handling below) removes any chance of another
      // listener or the browser's own handling running first.
      // FIX: e.code (physical key position, 'KeyA') is used instead of
      // e.key (the character produced) — with a non-Latin keyboard
      // layout active (e.g. Arabic), pressing the physical A key
      // produces a different character in e.key (confirmed by runtime
      // diagnostic: 'ش'), so the shortcut silently failed to match and
      // preventDefault() never ran, letting the browser's native
      // "select all page text" behavior fire instead. e.code is
      // layout-independent and always reports 'KeyA' for that physical
      // key regardless of the active input language.
      if (e.code === 'KeyA' && (e.ctrlKey || e.metaKey) && tool === 'select' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault()
        e.stopPropagation()
        const { existingMeasurements: measurements, hiddenIds: hidden, hiddenLayers: hiddenL, onMarqueeSelect: onMarquee } = latestRef.current
        const eligible = measurements.filter((m) => !hidden.has(m.id) && !hiddenL.has(m.measurement_type))
        onMarquee?.(eligible.map((m) => m.id), { additive: false })
        return
      }
      const shortcutTool = TOOLS.find((t) => t.shortcut.toLowerCase() === e.key.toLowerCase())
      if (shortcutTool && !e.ctrlKey && !e.metaKey && document.activeElement?.tagName !== 'INPUT') selectTool(shortcutTool.id)
    }
    function handleKeyUp(e) { if (e.code === 'Space') setSpaceHeld(false) }
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp)
    return () => { window.removeEventListener('keydown', handleKeyDown, true); window.removeEventListener('keyup', handleKeyUp) }
  }, [tool, points, history, redoStack, onSelectMeasurement, geometryEditHistory, geometryRedoStack, selectedMeasurementId, selectedVertex, zoomAreaStart])

  function cancelDrawing() { setPoints([]); setHistory([]); setRedoStack([]); setLivePreview(null) }

  // Overlap resolution rule (LOCKED, deterministic — documented tie-
  // breaker below): among ALL candidates whose hit-test matches the
  // point, AREA candidates (polygon/rectangle) are ranked by geometric
  // area — smallest wins, because that is the professional expectation
  // when a small shape sits inside a larger one. Linear/point
  // candidates are never compared by "area" (they have none); if no
  // area candidate matches, the first linear/point candidate in the
  // existing stable `existingMeasurements` order is used, exactly as
  // before. Tie-breaker for equal/near-equal areas: stable list order
  // (existingMeasurements index) — never database/query happenstance,
  // since the array itself is already ordered deterministically by the
  // caller (`created_at`).
  // ARCHITECTURAL FIX: reads existingMeasurements/zoom from latestRef —
  // never from render-scope closure variables — so the click always
  // hit-tests against the CURRENT data, eliminating the race window
  // where a stale closure could miss/misselect on the first click.
  function pickMeasurementAt(pdfPoint, radiusOverride) {
    const { existingMeasurements: measurements, zoom: z } = latestRef.current
    const radius = radiusOverride ?? (10 / z)
    const candidates = measurements
      .map((m, index) => ({ m, index }))
      .filter(({ m }) => isPointNearGeometry(pdfPoint, m.geometry_data, radius))
    if (candidates.length === 0) return null
    const areaCandidates = candidates
      .filter(({ m }) => m.geometry_data?.type === 'polygon' || m.geometry_data?.type === 'rectangle')
      .map(({ m, index }) => {
        const pts = m.geometry_data.points
        const polyPts = m.geometry_data.type === 'rectangle' ? rectangleCorners(pts) : pts
        return { m, index, area: polygonAreaForSelection(polyPts) }
      })
    if (areaCandidates.length > 0) {
      areaCandidates.sort((a, b) => (a.area - b.area) || (a.index - b.index))
      return areaCandidates[0].m
    }
    return candidates[0].m
  }

  // Dedicated Selection Interaction Surface handler (architectural fix).
  // This is the ONLY place that reads a pointer event for selection —
  // it is bound to one transparent surface that sits above everything,
  // so it never depends on which underlying child element (PDF <canvas>
  // tile, measurement <svg>, etc.) would otherwise have received the
  // browser event as event.target. Reuses the existing, proven
  // screenToPdf()/pickMeasurementAt() — no new coordinate system, no
  // new hit-testing logic. The FIRST click is always sufficient: both
  // screenToPdf and pickMeasurementAt read exclusively from latestRef,
  // never from a closure captured at an earlier render.
  function handleSelectionPointerDown(e) {
    const clicked = screenToPdf(e.clientX, e.clientY)
    const hit = pickMeasurementAt(clicked)
    if (!hit) { onSelectMeasurement?.(null); return } // Empty click ALWAYS clears everything, regardless of modifiers.
    // PHASE 2: forward the real modifier keys from the native event —
    // TakeoffCanvas stays unaware of multi-selection RULES (those live
    // entirely in TakeoffPage's applySelectionEvent); it only reports
    // what the user actually pressed.
    onSelectMeasurement?.(hit.id, { ctrlOrCmd: e.ctrlKey || e.metaKey, shift: e.shiftKey })
  }

  // Selection is handled by the dedicated interaction surface below
  // (handleSelectionPointerDown), not by this general handleClick — the
  // interaction surface guarantees the pointer event is received
  // reliably regardless of which underlying child (canvas/svg) would
  // otherwise have been event.target.
  function handleClick(e) {
    if (tool === 'select') return
    if (tool === 'window-select' || tool === 'crossing-select' || tool === 'lasso-select') return // PHASE 3/4 — marquee/lasso handled entirely via mousedown/mousemove/mouseup, not click.
    if (tool === 'pan' || spaceHeld) return

    const raw = screenToPdf(e.clientX, e.clientY)
    const pt = snapPdfPoint(raw)

    if (tool === 'calibrate') {
      const next = [...points, pt]
      setPoints(next)
      if (next.length === 2) { onCalibrate(pdfDistance(next[0], next[1])); setPoints([]) }
      return
    }
    if (tool === 'length' || tool === 'dimension') {
      const next = [...points, pt]
      setPoints(next)
      if (next.length === 2) finishLength(next, tool)
      return
    }
    if (tool === 'rectangle') {
      const next = [...points, pt]
      setPoints(next)
      if (next.length === 2) finishRectangle(next)
      return
    }
    if (tool === 'count') { finishCount(pt); return }

    if (tool === 'polyline' || tool === 'area') {
      if (tool === 'area' && points.length >= 3) {
        const first = points[0]
        if (Math.hypot(first.x - pt.x, first.y - pt.y) <= SNAP_RADIUS_PX / zoom) { finishMultiPoint(); return }
      }
      setHistory((h) => [...h, points])
      setRedoStack([])
      setPoints((p) => [...p, pt])
    }
  }

  function handleDoubleClick() {
    if ((tool === 'polyline' && points.length >= 2) || (tool === 'area' && points.length >= 3)) finishMultiPoint()
  }

  const rafPending = useRef(false)
  // Stable Interaction Controller: the hover pointer-tracking loop reads
  // and writes ONLY this ref between renders — never a value captured
  // from a previous React render. React state (setHoveredId) is
  // updated only as the final, minimal step, and only when the
  // resolved id actually changes, so hover never depends on stale
  // render-scope closures.
  const hoverRef = useRef(null)
  // PHASE 3 — Marquee (Window/Crossing Select) drag state. `marqueeStart`
  // is the PDF-space anchor point; `marqueeCurrent` updates live during
  // drag purely for the visual rectangle. Null when no drag is active.
  const [marqueeStart, setMarqueeStart] = useState(null)
  const [marqueeCurrent, setMarqueeCurrent] = useState(null)
  const marqueeAdditiveRef = useRef(false)
  // PHASE 4 — Lasso path points (PDF-space), accumulated during drag.
  const [lassoPoints, setLassoPoints] = useState(null)

  function handleMouseMove(e) {
    // Performance: throttle to one processed move per animation frame.
    // With large drawings and many measurements, processing every raw
    // mousemove event (which can fire far faster than 60Hz) causes visible
    // lag; this keeps interaction smooth without changing any geometry math.
    if (rafPending.current) return
    rafPending.current = true
    const clientX = e.clientX, clientY = e.clientY
    requestAnimationFrame(() => {
      rafPending.current = false
      processMouseMove(clientX, clientY)
    })
  }

  function processMouseMove(clientX, clientY) {
    const pt = screenToPdf(clientX, clientY)
    setCursorPdf(pt)
    if (isPanning && panStart.current) {
      setPan({ x: clientX - panStart.current.x, y: clientY - panStart.current.y })
      return
    }

    // PHASE 3 — live marquee rectangle update while dragging.
    if (marqueeStart) {
      setMarqueeCurrent(pt)
      return
    }

    // Zoom Area — live rectangle preview during drag. Purely a
    // viewport interaction — never touches selection, geometry, or
    // persistence.
    if (zoomAreaStart) {
      setZoomAreaCurrent(pt)
      return
    }

    // PHASE 4 — accumulate lasso path points live during drag.
    if (lassoPoints) {
      setLassoPoints((prev) => {
        const last = prev[prev.length - 1]
        if (Math.hypot(pt.x - last.x, pt.y - last.y) < 2 / zoom) return prev // skip near-duplicate points at this zoom
        return [...prev, pt]
      })
      return
    }

    if (draggingVertex) {
      const snapped = snapPdfPoint(pt)
      updateDraggedVertex(snapped)
      return
    }

    // Direct Drag-to-Move — body drag in progress. Compute the pixel
    // (screen-space) distance from the gesture's start point, in
    // client (CSS pixel) coordinates — deliberately NOT PDF-space, so
    // the threshold behaves identically regardless of the current
    // zoom level (a 4px screen-space nudge is the same physical mouse
    // movement whether zoomed in or out). Only once the threshold is
    // crossed does this become a live geometry translation; before
    // that, releasing the mouse must still register as a plain click
    // (handled in handleMouseUp), never as a Move.
    if (bodyDragRef.current) {
      const { startClientX, startClientY, measurementId } = bodyDragRef.current
      const screenDist = Math.hypot(clientX - startClientX, clientY - startClientY)
      if (screenDist >= DRAG_THRESHOLD_PX) {
        bodyDragRef.current.thresholdCrossed = true
        // Delta is computed once in PDF-space (via the same
        // screenToPdf used everywhere else) so it is correct at any
        // zoom/pan state, including if zoom/pan changed mid-drag.
        const startPt = screenToPdf(startClientX, startClientY)
        const currentPt = screenToPdf(clientX, clientY)
        setBodyDragLiveOffset({ measurementId, dx: currentPt.x - startPt.x, dy: currentPt.y - startPt.y })
      }
      return
    }

    // Stable Interaction Controller — Hover responsibility.
    // pickMeasurementAt() (the existing hit-test source of truth,
    // itself reading from latestRef) is evaluated on THIS pointer
    // position and written to hoverRef immediately. React state is
    // only touched when the resolved id differs from what hoverRef
    // already holds — this is the sole gate for re-rendering, and it
    // never consults the previous render's hoveredId value.
    if (tool === 'select') {
      const hit = pickMeasurementAt(pt)
      const nextHoverId = hit ? hit.id : null
      if (hoverRef.current !== nextHoverId) {
        hoverRef.current = nextHoverId
        setHoveredId(nextHoverId)
      }
    } else if (hoverRef.current !== null) {
      hoverRef.current = null
      setHoveredId(null)
    }

    // Live snap preview while a drawing tool is active (not select/pan)
    const snapPt = (tool !== 'select' && tool !== 'pan' && !spaceHeld) ? snapPdfPoint(pt) : (setSnapIndicator(null), pt)

    if (points.length > 0 && scaleFactor) {
      if ((tool === 'length' || tool === 'dimension') && points.length === 1) {
        setLivePreview({ type: 'length', value: pdfDistance(points[0], snapPt) * scaleFactor })
      } else if (tool === 'rectangle' && points.length === 1) {
        const w = Math.abs(snapPt.x - points[0].x) * scaleFactor
        const h = Math.abs(snapPt.y - points[0].y) * scaleFactor
        setLivePreview({ type: 'area', value: w * h })
      } else if ((tool === 'polyline' || tool === 'area') && points.length >= 1) {
        const preview = [...points, snapPt]
        if (tool === 'area' && preview.length >= 3) setLivePreview({ type: 'area', value: shoelaceArea(preview) * scaleFactor * scaleFactor })
        else if (tool === 'polyline') setLivePreview({ type: 'length', value: polylineLength(preview) * scaleFactor })
      }
    } else {
      setLivePreview(null)
    }
  }

  // Package 1b — Add Vertex. Only meaningful for free-form geometry
  // (polygon/polyline) where inserting a mid-edge point genuinely adds
  // a new editable corner. Rectangle is deliberately excluded — adding
  // a vertex would require converting it to a polygon, which is a type
  // change, not an edit, and is explicitly out of scope here.
  async function addVertexOnEdge(measurement, edgeIndex) {
    // Same staleness fix as removeVertex — re-read the current record.
    const current = latestRef.current.existingMeasurements.find((m) => m.id === measurement.id) || measurement
    const g = current.geometry_data
    if (g.type !== 'polygon' && g.type !== 'polyline') return
    const pts = g.points
    const a = pts[edgeIndex]
    const b = pts[(edgeIndex + 1) % pts.length]
    // polyline is open (no wrap edge past the last point) — reject an
    // out-of-range edge index rather than silently wrapping.
    if (g.type === 'polyline' && edgeIndex === pts.length - 1) return
    const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
    const newPts = [...pts.slice(0, edgeIndex + 1), midpoint, ...pts.slice(edgeIndex + 1)]
    await commitGeometryEdit(current.id, { ...g, points: newPts })
  }

  // Package 1b — Remove Vertex, with type-specific minimum-vertex
  // validation so no operation can produce geometry the existing
  // Measurement Engine functions (shoelaceArea/polylineLength/
  // pdfDistance) cannot compute. A polygon needs >=3 points to enclose
  // an area; a polyline/length/dimension needs >=2 points to have a
  // length at all. Rectangle is excluded — its 2 corner points define
  // its entire shape, so "removing a vertex" has no valid meaning for
  // it (this mirrors the Add Vertex exclusion above).
  // Package 1b UX revision — right-click on a vertex both selects it
  // and opens a minimal context menu with a single "Remove Vertex"
  // action. Replaces the double-click gesture entirely (it was
  // conflicting with the browser's own dblclick detection because
  // mousedown always fires drag-start first).
  function handleVertexContextMenu(e, measurement, vertexIndex) {
    e.preventDefault()
    e.stopPropagation()
    if (tool !== 'select') return
    setSelectedVertex({ measurementId: measurement.id, vertexIndex })
    setVertexContextMenu({ x: e.clientX, y: e.clientY, measurementId: measurement.id, vertexIndex })
  }

  async function removeVertex(measurement, vertexIndex) {
    // Re-read the CURRENT measurement by id from latestRef instead of
    // trusting the `measurement` object closed over by the JSX that
    // rendered this handle — that render may predate a just-completed
    // Add Vertex/Move on the SAME shape (React re-renders after
    // loadMeasurements() completes, which is asynchronous), so the
    // closed-over object's `id` can already be superseded by the time
    // the user acts.
    const current = latestRef.current.existingMeasurements.find((m) => m.id === measurement.id) || measurement
    const g = current.geometry_data
    if (g.type !== 'polygon' && g.type !== 'polyline' && g.type !== 'length' && g.type !== 'dimension') return
    const pts = g.points
    const minPoints = (g.type === 'polygon') ? 3 : 2
    if (pts.length <= minPoints) return // reject — would produce invalid/uncomputable geometry.
    const newPts = pts.filter((_, i) => i !== vertexIndex)
    setSelectedVertex(null)
    await commitGeometryEdit(current.id, { ...g, points: newPts })
  }

  // Package 1b — Move Complete Geometry: translates every point by the
  // same delta, preserving shape and size exactly. Works uniformly
  // across all geometry types (polygon/rectangle/polyline/length/
  // dimension use `points`; count uses a single `point`) since it only
  // ever adds the same (dx, dy) to existing PDF-space coordinates —
  // it never touches zoom/pan/calibration/the coordinate transform
  // itself, which remain exactly as they are.
  async function moveGeometry(measurement, dx, dy) {
    const g = measurement.geometry_data
    const translate = (p) => ({ ...p, x: p.x + dx, y: p.y + dy })
    const newGeom = g.points
      ? { ...g, points: g.points.map(translate) }
      : { ...g, point: translate(g.point) }
    await commitGeometryEdit(measurement.id, newGeom)
  }

  // Package 1c — Rotation, ±90° only, around the geometry's own
  // centroid (never the Canvas/Sheet origin). Rectangle is a special
  // case: its geometry_data stores only two diagonal corner points
  // (Axis-Aligned), so an arbitrary-angle rotation would require
  // converting it to a free 4-point polygon — a TYPE change, out of
  // scope here. At exactly ±90°, an axis-aligned rectangle rotates
  // into another axis-aligned rectangle (width/height swap around the
  // center), so it stays a valid 'rectangle' with the SAME 2-point
  // representation — no type conversion needed for this specific,
  // limited case. count has no orientation, so it is a no-op.
  async function rotateGeometry(measurement, angleDeg) {
    const g = measurement.geometry_data
    const angleRad = (angleDeg * Math.PI) / 180
    if (g.type === 'count') return // no meaningful orientation to rotate.
    if (g.type === 'rectangle') {
      if (Math.abs(Math.abs(angleDeg) - 90) > 0.001) return // only ±90° supported for rectangle, per the type-preservation constraint above.
      const corners = rectangleCorners(g.points)
      const center = centroidOf(corners)
      const rotatedCorners = corners.map((p) => rotatePoint(p, center, angleRad))
      // Re-derive a valid axis-aligned 2-point diagonal from the
      // rotated corners' bounding box — at exactly ±90° this exactly
      // reproduces the swapped-dimension rectangle, since rotating an
      // axis-aligned box by a right angle yields another axis-aligned box.
      const xs = rotatedCorners.map((p) => p.x), ys = rotatedCorners.map((p) => p.y)
      const newPoints = [{ x: Math.min(...xs), y: Math.min(...ys) }, { x: Math.max(...xs), y: Math.max(...ys) }]
      await commitGeometryEdit(measurement.id, { ...g, points: newPoints })
      return
    }
    // polygon / polyline / length / dimension — general case.
    const center = centroidOf(g.points)
    const newPoints = g.points.map((p) => rotatePoint(p, center, angleRad))
    await commitGeometryEdit(measurement.id, { ...g, points: newPoints })
  }

  function handleVertexPointerDown(e, measurement, vertexIndex) {
    e.stopPropagation()
    if (tool !== 'select') return
    if (e.button === 2) return // FIX: right-click also fires mousedown — starting a drag here interfered with the subsequent contextmenu event. Left-click (button 0) only.
    if (lockedIds.has(measurement.id)) return // Package 3 — locked: block starting a vertex drag entirely (selection itself is untouched, handled elsewhere).
    setSelectedVertex({ measurementId: measurement.id, vertexIndex }) // left-click also marks this vertex as the one Delete/Backspace will act on.
    finishingDragRef.current = false // reset for this new gesture.
    setDraggingVertex({ measurementId: measurement.id, vertexIndex, geometryType: measurement.geometry_data.type })
    setEditedGeometry({ ...measurement.geometry_data })
  }

  // Direct Drag-to-Move — pointerdown on the geometry BODY (not a
  // vertex). Does NOT immediately start a move: it only arms
  // bodyDragRef with the starting point. The actual distinction
  // between "this was a click" (selection only) and "this was a drag"
  // (Move Complete Geometry) is resolved in processMouseMove via the
  // pixel threshold, and finalized in handleMouseUp — this mirrors
  // exactly how vertex drag and marquee/lasso already separate
  // gesture-start from gesture-classification in this file.
  function handleGeometryBodyPointerDown(e, measurement) {
    if (tool !== 'select') return
    if (e.button === 2) return // right-click is reserved for the vertex context menu elsewhere; body drag is left-click only.
    if (lockedIds.has(measurement.id)) return // Package 3 — locked: do NOT stopPropagation here, so the click falls through to the normal Select interaction surface below and the measurement remains fully selectable, just not draggable.
    e.stopPropagation() // critical: must win over the underlying full-page Select interaction surface's onClick, exactly like vertex handles already do.
    bodyDragRef.current = { measurementId: measurement.id, startClientX: e.clientX, startClientY: e.clientY, thresholdCrossed: false }
  }

  function updateDraggedVertex(newPt) {
    setEditedGeometry((prev) => {
      if (!prev) return prev
      const pts = [...(prev.points || [prev.point])]
      pts[draggingVertex.vertexIndex] = newPt
      return { ...prev, points: prev.points ? pts : undefined, point: prev.point ? pts[0] : undefined }
    })
  }

  function computeValueFromGeometry(geometryData) {
    if (geometryData.type === 'polygon') return shoelaceArea(geometryData.points) * scaleFactor * scaleFactor
    if (geometryData.type === 'rectangle') {
      const w = Math.abs(geometryData.points[1].x - geometryData.points[0].x)
      const h = Math.abs(geometryData.points[1].y - geometryData.points[0].y)
      return w * scaleFactor * (h * scaleFactor)
    }
    if (geometryData.type === 'length' || geometryData.type === 'dimension') return pdfDistance(geometryData.points[0], geometryData.points[1]) * scaleFactor
    if (geometryData.type === 'polyline') return polylineLength(geometryData.points) * scaleFactor
    return null
  }

  // Package 1b — shared commit path. Every geometry mutation (vertex
  // drag, Add Vertex, Remove Vertex, Move) funnels through this SAME
  // function, which is exactly the Package 1a flow: history entry →
  // onGeometryEdited → handleGeometryEdited → Atomic Supersede →
  // newRow.id → history reference rewrite. No new persistence path.
  // Package 1c — Duplicate, same Sheet only. Deliberately NOT routed
  // through commitGeometryEdit/Supersede — this creates a genuinely
  // NEW, independent measurement (a second active record for the same
  // takeoff_item, which the audited architecture explicitly treats as
  // legitimate), not a new version of the existing one. Reuses
  // computeValueFromGeometry (no new calculation) and delegates actual
  // persistence to onDuplicateMeasurement, which mirrors the exact
  // existing insert path in TakeoffPage.jsx (handleMeasurementComplete)
  // — no parallel creation path.
  async function duplicateMeasurement(measurement) {
    if (lockedIds.has(measurement.id)) return // Package 3 — locked measurements cannot be duplicated.
    const g = measurement.geometry_data
    const OFFSET = 20 // PDF-space units — enough to be visually distinguishable at typical zoom, independent of zoom itself.
    const offsetPt = (p) => ({ ...p, x: p.x + OFFSET, y: p.y + OFFSET })
    const newGeom = g.points
      ? { ...g, points: g.points.map(offsetPt) }
      : { ...g, point: offsetPt(g.point) }
    const newValue = computeValueFromGeometry(newGeom)
    // computeValueFromGeometry returns null for 'count' (single point,
    // no area/length to compute) — for count, the ORIGINAL value (1)
    // is reused directly, since a duplicated count marker still
    // represents exactly one object.
    const valueToUse = newValue !== null ? newValue : measurement.calculated_value
    await onDuplicateMeasurement?.(measurement.sheet_id, measurement.takeoff_item_id, measurement.calibration_id, measurement.measurement_type, newGeom, valueToUse)
  }

  // Package 2 — Toggle Polarity. Deliberately NOT routed through
  // commitGeometryEdit/Supersede: polarity is a classification field,
  // not a geometry mutation — geometry_data and calculated_value are
  // untouched. Direct persistence via onTogglePolarity, mirroring how
  // duplicateMeasurement delegates to its own dedicated callback
  // rather than reusing the geometry-edit pipeline.
  async function togglePolarity(measurement) {
    const newPolarity = measurement.polarity === 'negative' ? 'positive' : 'negative'
    await onTogglePolarity?.(measurement.id, newPolarity)
  }

  async function commitGeometryEdit(measurementId, newGeometryData) {
    if (lockedIds.has(measurementId)) return null // Package 3 — locked measurements cannot be mutated. Selection/inspection remain unaffected since this check is only in the mutation path, never in selection/hover logic.
    const newValue = computeValueFromGeometry(newGeometryData)
    if (newValue === null) return null
    const original = existingMeasurements.find((m) => m.id === measurementId)
    if (original) {
      setGeometryEditHistory((h) => [...h, {
        measurementId,
        previousGeometryData: original.geometry_data,
        previousCalculatedValue: original.calculated_value,
        newGeometryData,
        newCalculatedValue: newValue,
      }])
      setGeometryRedoStack([])
    }
    setEditInFlight(true)
    try {
      const newId = await onGeometryEdited?.(measurementId, newGeometryData, newValue)
      if (newId) {
        setGeometryEditHistory((h) => h.map((e) => (e.measurementId === measurementId ? { ...e, measurementId: newId } : e)))
        setGeometryRedoStack((r) => r.map((e) => (e.measurementId === measurementId ? { ...e, measurementId: newId } : e)))
      }
      return newId
    } finally {
      setEditInFlight(false)
    }
  }

  async function finishVertexDrag() {
    if (!draggingVertex || !editedGeometry) return
    if (finishingDragRef.current) return // already processing this exact gesture's end — reject the duplicate call synchronously.
    finishingDragRef.current = true
    await commitGeometryEdit(draggingVertex.measurementId, editedGeometry)
    setDraggingVertex(null)
    setEditedGeometry(null)
  }

  // P0 — Undo/Redo for existing-geometry edits. Each call performs a
  // real Supersede (via the existing onGeometryEdited → handleSupersede
  // path already used for normal edits) that restores the previous or
  // next geometry/value as a NEW measurement record — the historical
  // records are never mutated or deleted, preserving the existing
  // append-only Provenance/Supersede model exactly as audited.
  // NOTE: guards below are retained as defensive protection only (per
  // instruction 8) — they are NOT the fix for the stale-id root cause;
  // the fix is the reference update after a successful Supersede.
  async function undoGeometryEdit() {
    if (undoInProgressRef.current) return
    if (geometryEditHistory.length === 0) return
    undoInProgressRef.current = true
    try {
      const entry = geometryEditHistory[geometryEditHistory.length - 1]
      setGeometryEditHistory((h) => h.slice(0, -1))
      setGeometryRedoStack((r) => [...r, entry])
      const newId = await onGeometryEdited?.(entry.measurementId, entry.previousGeometryData, entry.previousCalculatedValue)
      // (I)/(J): only touch history/redo state on a CONFIRMED successful
      // Supersede (non-null newId, meaning the RPC committed). A failed
      // or rolled-back attempt leaves every reference exactly as it was.
      if (newId) {
        const staleId = entry.measurementId
        setGeometryEditHistory((h) => h.map((e) => (e.measurementId === staleId ? { ...e, measurementId: newId } : e)))
        setGeometryRedoStack((r) => r.map((e) => (e.measurementId === staleId ? { ...e, measurementId: newId } : e)))
      }
    } finally {
      undoInProgressRef.current = false
    }
  }
  async function redoGeometryEdit() {
    if (redoInProgressRef.current) return
    if (geometryRedoStack.length === 0) return
    redoInProgressRef.current = true
    try {
      const entry = geometryRedoStack[geometryRedoStack.length - 1]
      setGeometryRedoStack((r) => r.slice(0, -1))
      setGeometryEditHistory((h) => [...h, entry])
      const newId = await onGeometryEdited?.(entry.measurementId, entry.newGeometryData, entry.newCalculatedValue)
      if (newId) {
        const staleId = entry.measurementId
        setGeometryEditHistory((h) => h.map((e) => (e.measurementId === staleId ? { ...e, measurementId: newId } : e)))
        setGeometryRedoStack((r) => r.map((e) => (e.measurementId === staleId ? { ...e, measurementId: newId } : e)))
      }
    } finally {
      redoInProgressRef.current = false
    }
  }

  function handleMouseDown(e) {
    if (tool === 'pan' || spaceHeld || e.button === 1) {
      setIsPanning(true)
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }
      e.preventDefault()
      return
    }
    // Zoom Area — arm the drag start using the SAME screenToPdf
    // conversion — no new coordinate system. Click-vs-drag is resolved
    // in handleMouseUp via DRAG_THRESHOLD_PX (reused, not duplicated).
    if (tool === 'zoom-area') {
      zoomAreaStartClientRef.current = { x: e.clientX, y: e.clientY }
      const pt = screenToPdf(e.clientX, e.clientY)
      setZoomAreaStart(pt)
      setZoomAreaCurrent(pt)
      return
    }
    // PHASE 3 — Marquee start. Only from empty space: if the same
    // click-point already hits an existing measurement, defer entirely
    // to the existing single/multi-select click path (handled by the
    // Select interaction surface's onClick) — this preserves the
    // existing Select/Ctrl/Shift+Click behavior and never interferes
    // with Vertex Editing (which has its own onMouseDown on the vertex
    // handles and stops propagation there already).
    if (tool === 'window-select' || tool === 'crossing-select') {
      const pt = screenToPdf(e.clientX, e.clientY)
      const hit = pickMeasurementAt(pt)
      if (hit) {
        // FIX: onClick is disabled entirely for these two tools (see
        // handleClick above), so falling through here would silently do
        // nothing. Starting on a measurement must still select it —
        // same modifier semantics as the normal Select click.
        onSelectMeasurement?.(hit.id, { ctrlOrCmd: e.ctrlKey || e.metaKey, shift: e.shiftKey })
        return
      }
      marqueeAdditiveRef.current = e.ctrlKey || e.metaKey
      setMarqueeStart(pt)
      setMarqueeCurrent(pt)
    }
    // PHASE 4 — Lasso start. Same empty-space-only rule as Marquee.
    if (tool === 'lasso-select') {
      const pt = screenToPdf(e.clientX, e.clientY)
      const hit = pickMeasurementAt(pt)
      if (hit) {
        onSelectMeasurement?.(hit.id, { ctrlOrCmd: e.ctrlKey || e.metaKey, shift: e.shiftKey })
        return
      }
      marqueeAdditiveRef.current = e.ctrlKey || e.metaKey
      setLassoPoints([pt])
    }
  }
  function handleMouseUp(e) {
    setIsPanning(false); panStart.current = null
    if (draggingVertex) finishVertexDrag()
    // Zoom Area — finalize. Uses the SAME DRAG_THRESHOLD_PX as
    // Body-Drag-to-Move (reused, not duplicated). Below threshold =
    // treated as a plain click with no viewport change.
    if (zoomAreaStart && zoomAreaCurrent) {
      const startClient = zoomAreaStartClientRef.current
      const screenDist = e ? Math.hypot(e.clientX - startClient.x, e.clientY - startClient.y) : 0
      if (screenDist >= DRAG_THRESHOLD_PX) {
        const rectMinX = Math.min(zoomAreaStart.x, zoomAreaCurrent.x)
        const rectMaxX = Math.max(zoomAreaStart.x, zoomAreaCurrent.x)
        const rectMinY = Math.min(zoomAreaStart.y, zoomAreaCurrent.y)
        const rectMaxY = Math.max(zoomAreaStart.y, zoomAreaCurrent.y)
        const rectWidth = rectMaxX - rectMinX
        const rectHeight = rectMaxY - rectMinY
        if (rectWidth > 0 && rectHeight > 0 && containerRef.current) {
          // Exactly the fitDrawing() calculation pattern (min of the
          // two axis ratios, preserving aspect ratio), against the
          // selected rectangle's own dimensions and the ACTUAL current
          // container size — not any hardcoded dimension.
          const w = containerRef.current.clientWidth
          const h = containerRef.current.clientHeight
          const zw = (w - 60) / rectWidth
          const zh = (h - 60) / rectHeight
          const rawZoom = Math.min(zw, zh)
          const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, rawZoom))
          const centerX = (rectMinX + rectMaxX) / 2
          const centerY = (rectMinY + rectMaxY) / 2
          // Same setZoom/setPan system every other viewport operation
          // in this file already uses — no second transform mechanism.
          setZoom(newZoom)
          setPan({ x: w / 2 - centerX * newZoom, y: h / 2 - centerY * newZoom })
        }
      }
      setZoomAreaStart(null)
      setZoomAreaCurrent(null)
      zoomAreaStartClientRef.current = null
      return
    }
    // Direct Drag-to-Move — finalize. If the threshold was never
    // crossed, this was just a click on the shape's body: perform the
    // SAME selection the full-page Select surface's onClick would have
    // done (that onClick was pre-empted by stopPropagation in
    // handleGeometryBodyPointerDown, so it must be replicated here
    // explicitly — not silently dropped). If the threshold WAS
    // crossed, commit exactly ONE geometry edit for the whole gesture
    // via the existing Package 1a/1b commitGeometryEdit pipeline —
    // never a mutation per pointermove.
    if (bodyDragRef.current) {
      const { measurementId, thresholdCrossed } = bodyDragRef.current
      if (thresholdCrossed && bodyDragLiveOffset) {
        const m = latestRef.current.existingMeasurements.find((mm) => mm.id === measurementId)
        if (m) moveGeometry(m, bodyDragLiveOffset.dx, bodyDragLiveOffset.dy)
      } else if (e) {
        onSelectMeasurement?.(measurementId, { ctrlOrCmd: e.ctrlKey || e.metaKey, shift: e.shiftKey })
      }
      bodyDragRef.current = null
      setBodyDragLiveOffset(null)
    }
    // PHASE 3 — finalize marquee selection.
    if (marqueeStart && marqueeCurrent) {
      const box = {
        x1: Math.min(marqueeStart.x, marqueeCurrent.x), x2: Math.max(marqueeStart.x, marqueeCurrent.x),
        y1: Math.min(marqueeStart.y, marqueeCurrent.y), y2: Math.max(marqueeStart.y, marqueeCurrent.y),
      }
      const eligible = existingMeasurements.filter(isVisible)
      const predicate = tool === 'window-select' ? isGeometryFullyContainedInBox : isGeometryIntersectingBox
      const matchedIds = eligible.filter((m) => m.geometry_data && predicate(m.geometry_data, box)).map((m) => m.id)
      onMarqueeSelect?.(matchedIds, { additive: marqueeAdditiveRef.current })
      setMarqueeStart(null)
      setMarqueeCurrent(null)
    }
    // PHASE 4 — finalize lasso selection. Auto-closes the path (the
    // isPointInPolygon/segmentIntersects primitives already treat the
    // point list as a closed ring via modulo indexing, so no explicit
    // "add first point at the end" step is needed). Reuses the same
    // Crossing-vs-Window intent: a plain lasso behaves like Crossing
    // (any intersection selects) since that is the professional CAD
    // expectation for a freeform loop with no separate lasso variants.
    if (lassoPoints && lassoPoints.length >= 3) {
      const simplified = simplifyLassoPath(lassoPoints)
      const eligible = existingMeasurements.filter(isVisible)
      const matchedIds = eligible.filter((m) => m.geometry_data && isGeometryIntersectingLasso(m.geometry_data, simplified)).map((m) => m.id)
      onMarqueeSelect?.(matchedIds, { additive: marqueeAdditiveRef.current })
      setLassoPoints(null)
    } else if (lassoPoints) {
      setLassoPoints(null) // degenerate lasso (too few points) — cancel without changing selection.
    }
  }

  function handleWheel(e) {
    e.preventDefault()
    const rect = containerRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    const pdfBefore = { x: (cx - pan.x) / zoom, y: (cy - pan.y) / zoom }
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom + delta))
    setZoom(newZoom)
    setPan({ x: cx - pdfBefore.x * newZoom, y: cy - pdfBefore.y * newZoom })
  }

  function zoomBy(delta) { setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta))) }

  function fitWidth() {
    if (!containerRef.current) return
    const w = containerRef.current.clientWidth
    setZoom((w - 40) / PAGE_BASE_WIDTH)
    setPan((p) => ({ x: 20, y: p.y }))
  }
  function fitHeight() {
    if (!containerRef.current) return
    const h = containerRef.current.clientHeight
    setZoom((h - 40) / PAGE_BASE_HEIGHT)
    setPan((p) => ({ x: p.x, y: 20 }))
  }
  function fitDrawing() {
    if (!containerRef.current) return
    const w = containerRef.current.clientWidth
    const h = containerRef.current.clientHeight
    const zw = (w - 60) / PAGE_BASE_WIDTH
    const zh = (h - 60) / PAGE_BASE_HEIGHT
    const newZoom = Math.min(zw, zh)
    setZoom(newZoom)
    setPan({ x: (w - PAGE_BASE_WIDTH * newZoom) / 2, y: 20 })
  }
  function actualSize() { setZoom(1); setPan({ x: 40, y: 20 }) }

  function pdfDistance(a, b) { return Math.hypot(b.x - a.x, b.y - a.y) }
  function polylineLength(pts) { let t = 0; for (let i = 0; i < pts.length - 1; i++) t += pdfDistance(pts[i], pts[i + 1]); return t }
  function shoelaceArea(pts) {
    let sum = 0
    for (let i = 0; i < pts.length; i++) { const j = (i + 1) % pts.length; sum += pts[i].x * pts[j].y - pts[j].x * pts[i].y }
    return Math.abs(sum / 2)
  }

  function requireCalibration() {
    if (!scaleFactor) { alert('Calibrate this sheet before drawing measurements.'); return false }
    return true
  }

  function finishLength(pts, sourceTool) {
    if (!requireCalibration()) { setPoints([]); return }
    const d = pdfDistance(pts[0], pts[1])
    onMeasurementComplete({ measurementType: 'length', calculatedValue: d * scaleFactor, geometryData: { type: sourceTool === 'dimension' ? 'dimension' : 'length', points: pts, pdf_distance: d } }, { testMode })
    setPoints([]); setLivePreview(null); setHistory([]); setRedoStack([])
  }
  function finishRectangle(pts) {
    if (!requireCalibration()) { setPoints([]); return }
    const w = Math.abs(pts[1].x - pts[0].x)
    const h = Math.abs(pts[1].y - pts[0].y)
    onMeasurementComplete({ measurementType: 'area', calculatedValue: w * scaleFactor * (h * scaleFactor), geometryData: { type: 'rectangle', points: pts, pdf_width: w, pdf_height: h } }, { testMode })
    setPoints([]); setLivePreview(null); setHistory([]); setRedoStack([])
  }
  function isOutOfBounds(pts) {
    return pts.some((p) => p.x < -5 || p.y < -5 || p.x > PAGE_BASE_WIDTH + 5 || p.y > PAGE_BASE_HEIGHT + 5)
  }

  function finishMultiPoint() {
    if (!requireCalibration()) { setPoints([]); return }
    if (points.length < (tool === 'area' ? 3 : 2)) {
      alert(`Incomplete measurement — ${tool === 'area' ? 'an area needs at least 3 points' : 'a line needs at least 2 points'}.`)
      setPoints([]); return
    }
    if (isOutOfBounds(points)) {
      if (!window.confirm('Some points are outside the visible page bounds — continue anyway?')) { setPoints([]); return }
    }
    if (tool === 'area') {
      const pdfArea = shoelaceArea(points)
      if (pdfArea < 1e-6) {
        alert('Invalid geometry — the points are collinear and enclose zero area. Check your clicks.')
        setPoints([]); return
      }
      onMeasurementComplete({ measurementType: 'area', calculatedValue: pdfArea * scaleFactor * scaleFactor, geometryData: { type: 'polygon', points, pdf_area: pdfArea } }, { testMode })
    } else {
      const pdfLen = polylineLength(points)
      onMeasurementComplete({ measurementType: 'length', calculatedValue: pdfLen * scaleFactor, geometryData: { type: 'polyline', points, pdf_length: pdfLen } }, { testMode })
    }
    setPoints([]); setLivePreview(null); setHistory([]); setRedoStack([])
  }
  function finishCount(pt) {
    onMeasurementComplete({ measurementType: 'count', calculatedValue: 1, geometryData: { type: 'count', point: pt } }, { testMode })
  }

  function handleUndo() {
    if (points.length === 0) return
    setRedoStack((r) => [...r, points])
    if (history.length === 0) { setPoints([]); return }
    setPoints(history[history.length - 1])
    setHistory((h) => h.slice(0, -1))
  }
  function handleRedo() {
    if (redoStack.length === 0) return
    setHistory((h) => [...h, points])
    setPoints(redoStack[redoStack.length - 1])
    setRedoStack((r) => r.slice(0, -1))
  }

  function selectTool(t) {
    setTool(t)
    setPoints([]); setLivePreview(null); setHistory([]); setRedoStack([])
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <Toolbar
        tool={tool} setTool={selectTool} zoom={zoom} zoomBy={zoomBy}
        onFitWidth={fitWidth} onFitHeight={fitHeight} onFitDrawing={fitDrawing} onActualSize={actualSize}
        calibrated={!!calibration}
        onUndo={() => { if (geometryEditHistory.length > 0) undoGeometryEdit(); else handleUndo() }} canUndo={points.length > 0 || geometryEditHistory.length > 0}
        onRedo={() => { if (geometryRedoStack.length > 0) redoGeometryEdit(); else handleRedo() }} canRedo={redoStack.length > 0 || geometryRedoStack.length > 0}
        onCancel={cancelDrawing}
        onDeleteSelected={() => selectedMeasurementId && onSelectMeasurement?.(null, { requestDelete: true })}
        hasSelection={!!selectedMeasurementId && !editInFlight}
        onSubmitReview={() => onSubmitForReview?.(selectedMeasurementIds)}
        hasBatchSelection={selectedMeasurementIds.length > 0 && !editInFlight}
        gridOn={gridOn} setGridOn={setGridOn} snapOn={snapOn} setSnapOn={setSnapOn}
        onDuplicate={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) duplicateMeasurement(m) }}
        onRotateLeft={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) rotateGeometry(m, -90) }}
        onRotateRight={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) rotateGeometry(m, 90) }}
        onMoveUp={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) moveGeometry(m, 0, -1) }}
        onMoveDown={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) moveGeometry(m, 0, 1) }}
        onMoveLeft={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) moveGeometry(m, -1, 0) }}
        onMoveRight={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) moveGeometry(m, 1, 0) }}
        onTogglePolarity={() => { const m = latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId); if (m) togglePolarity(m) }}
        isNegative={latestRef.current.existingMeasurements.find((mm) => mm.id === selectedMeasurementId)?.polarity === 'negative'}
        isolateActive={isolateActive}
        onToggleIsolate={toggleIsolate}
        isLocked={lockedIds.has(selectedMeasurementId)}
        onToggleLock={() => selectedMeasurementId && toggleLock(selectedMeasurementId)}
        fullCanvasMode={fullCanvasMode}
        onToggleFullCanvas={() => setFullCanvasMode?.((prev) => !prev)}
      />

      {pdfError && (
        <div style={{ padding: '8px 14px', background: colors.dangerBg, color: colors.danger, fontSize: 13 }}>
          ⚠ PDF failed to load: {pdfError}
        </div>
      )}

      {!calibration && (
        <div style={{ padding: '8px 14px', background: colors.warningBg, color: colors.warningText, fontSize: 13 }}>
          ⚠ Not calibrated — use the Calibrate tool: click two points a known real-world distance apart.
        </div>
      )}

      <div
        ref={containerRef}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{
          position: 'relative', overflow: 'hidden', background: '#EAECEF', flex: 1, minHeight: 500,
          cursor: (tool === 'pan' || spaceHeld) ? (isPanning ? 'grabbing' : 'grab') : (tool === 'select' || tool === 'window-select' || tool === 'crossing-select' || tool === 'lasso-select') ? 'default' : 'crosshair',
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
          {fileUrl && fileType === 'pdf' ? (
            <PdfTileLayer

              fileUrl={fileUrl}
              pageIndex={pageIndex}
              pageWidth={PAGE_BASE_WIDTH}
              pageHeight={PAGE_BASE_HEIGHT}
              zoom={zoom}
              viewportRect={viewportRect}
            />
          ) : fileUrl && fileType === 'dxf' ? (
            <div style={{ width: PAGE_BASE_WIDTH, height: PAGE_BASE_HEIGHT, background: '#fff' }}>
              <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0 }}>
                <DxfLayer entities={dxfEntities} zoom={zoom} />
              </svg>
              {dxfLoadError && (
                <div style={{ position: 'absolute', top: 12, left: 12, background: colors.dangerBg, color: colors.danger, padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
                  DXF parse error: {dxfLoadError}
                </div>
              )}
            </div>
          ) : (
            <div style={{ width: PAGE_BASE_WIDTH, height: PAGE_BASE_HEIGHT, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted, fontSize: 14 }}>
              No PDF uploaded for this drawing version yet.
            </div>
          )}

          <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
            {gridOn && <GridLines width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} spacing={gridSpacing} zoom={zoom} />}

            {existingMeasurements.filter(isVisible).map((m, idx) => {
              const isBeingEdited = draggingVertex?.measurementId === m.id && editedGeometry
              const isBeingBodyDragged = bodyDragLiveOffset?.measurementId === m.id
              let displayMeasurement = isBeingEdited ? { ...m, geometry_data: editedGeometry } : m
              if (isBeingBodyDragged) {
                const g = displayMeasurement.geometry_data
                const shift = (p) => ({ ...p, x: p.x + bodyDragLiveOffset.dx, y: p.y + bodyDragLiveOffset.dy })
                displayMeasurement = { ...displayMeasurement, geometry_data: g.points ? { ...g, points: g.points.map(shift) } : { ...g, point: shift(g.point) } }
              }
              const isSelected = selectedMeasurementIds.includes(m.id) || selectedMeasurementId === m.id
              return (
                <g key={m.id}>
                  <g opacity={opacityMap.has(m.id) ? opacityMap.get(m.id) : 1}>
                    <MeasurementGeometry measurement={{ ...displayMeasurement, displayIndex: existingMeasurements.filter((x) => x.measurement_type === 'count').indexOf(m) + 1 }} selected={isSelected} isPrimary={selectedMeasurementId === m.id} hovered={hoveredId === m.id && !isSelected} zoom={zoom} />
                  </g>
                </g>
              )
            })}
            {/* Direct Drag-to-Move — a second consistency pass just to
                keep body-drag live-preview logic co-located with the
                first pass above; the actual interactive hit target is
                rendered in its own <svg> further below, AFTER the
                full-page Select interaction surface, so it wins
                hit-testing over that surface — the same reasoning
                originally used to justify that surface's own DOM
                position, now applied in reverse for this new target. */}

            {points.length >= 2 && (tool === 'area' || tool === 'polyline') && (
              <polyline points={points.map((p) => `${p.x},${p.y}`).join(' ')} fill={tool === 'area' ? 'rgba(27,58,107,0.15)' : 'none'} stroke={colors.primary} strokeWidth={2 / zoom} />
            )}
            {points.length === 1 && cursorPdf && (tool === 'length' || tool === 'dimension' || tool === 'polyline' || tool === 'area') && (
              <line x1={points[0].x} y1={points[0].y} x2={cursorPdf.x} y2={cursorPdf.y} stroke={colors.primary} strokeWidth={1.5 / zoom} strokeDasharray={`${4 / zoom} ${4 / zoom}`} />
            )}
            {points.length === 1 && cursorPdf && tool === 'rectangle' && (
              <rect x={Math.min(points[0].x, cursorPdf.x)} y={Math.min(points[0].y, cursorPdf.y)}
                width={Math.abs(cursorPdf.x - points[0].x)} height={Math.abs(cursorPdf.y - points[0].y)}
                fill="rgba(27,58,107,0.12)" stroke={colors.primary} strokeWidth={1.5 / zoom} strokeDasharray={`${4 / zoom} ${4 / zoom}`} />
            )}
            {points.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={4 / zoom} fill={colors.primary} />)}
            {points.length >= 3 && tool === 'area' && <circle cx={points[0].x} cy={points[0].y} r={8 / zoom} fill="none" stroke={colors.accentGreen} strokeWidth={2 / zoom} />}

            {cursorPdf && tool !== 'select' && tool !== 'pan' && (
              <>
                <line x1={cursorPdf.x} y1={0} x2={cursorPdf.x} y2={PAGE_BASE_HEIGHT} stroke="rgba(27,58,107,0.3)" strokeWidth={1 / zoom} />
                <line x1={0} y1={cursorPdf.y} x2={PAGE_BASE_WIDTH} y2={cursorPdf.y} stroke="rgba(27,58,107,0.3)" strokeWidth={1 / zoom} />
              </>
            )}

            {snapIndicator && <SnapIndicator indicator={snapIndicator} zoom={zoom} />}
          </svg>

          {/* Dedicated Selection Interaction Surface (architectural fix).
              Sits inside the SAME pan/zoom-transformed wrapper as the PDF
              tiles and the measurement <svg> above, so it shares the
              exact same coordinate space — screenToPdf() needs no
              changes. It is the ONLY element responsible for click-based
              selection when tool === 'select' (both selecting a new
              measurement AND clearing selection on empty space),
              guaranteeing the click is received here regardless of
              which underlying child (PDF <canvas> tile, measurement
              <svg> shape) would otherwise have been event.target. For
              every other tool it is fully transparent, so pan/zoom/
              drawing are unaffected. */}
          <div
            onClick={tool === 'select' ? handleSelectionPointerDown : undefined}
            style={{
              position: 'absolute', top: 0, left: 0, width: PAGE_BASE_WIDTH, height: PAGE_BASE_HEIGHT,
              background: 'transparent', pointerEvents: tool === 'select' ? 'auto' : 'none',
            }}
          />

          {/* Direct Drag-to-Move — body-drag hit target. Placed AFTER
              the full-page Select interaction surface above, so it
              wins hit-testing over that surface for the currently-
              selected measurement's body — the surface above still
              handles clicks on empty space and every other
              measurement normally. */}
          {tool === 'select' && selectedMeasurementId && !editInFlight && (
            <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {existingMeasurements.filter((m) => m.id === selectedMeasurementId).map((m) => {
                if (!m.geometry_data.points) return null
                const pts = m.geometry_data.type === 'rectangle' ? rectangleCorners(m.geometry_data.points) : m.geometry_data.points
                return (
                  <polygon
                    key={`body-drag-${m.id}`}
                    points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill="transparent" stroke="none"
                    style={{ pointerEvents: 'auto', cursor: 'move' }}
                    onMouseDown={(e) => handleGeometryBodyPointerDown(e, m)}
                  />
                )
              })}
            </svg>
          )}

          {/* Vertex-handle layer — deliberately a SEPARATE <svg>, placed
              AFTER (and therefore visually/interactively above) the
              selection interaction surface, so dragging a vertex of the
              already-selected measurement is never blocked by that
              surface. Only rendered when something is selected. */}
          {tool === 'select' && selectedMeasurementId && !editInFlight && (
            <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {existingMeasurements.filter((m) => m.id === selectedMeasurementId).map((m) => {
                const isBeingEdited = draggingVertex?.measurementId === m.id && editedGeometry
                if (!m.geometry_data.points) return null
                const pts = isBeingEdited ? editedGeometry.points : m.geometry_data.points
                const g = m.geometry_data
                const isClosed = g.type === 'polygon' || g.type === 'rectangle'
                return [
                  // Package 1b — Add Vertex: a small mid-edge handle
                  // between each pair of consecutive points, double-
                  // click inserts a new vertex there. Only rendered for
                  // polygon/polyline (addVertexOnEdge itself also
                  // guards this, so this is a UI-level mirror of the
                  // same rule, not a second source of truth).
                  (g.type === 'polygon' || g.type === 'polyline') && pts.slice(0, isClosed ? pts.length : pts.length - 1).map((v, i) => {
                    const next = pts[(i + 1) % pts.length]
                    const mid = { x: (v.x + next.x) / 2, y: (v.y + next.y) / 2 }
                    return (
                      <rect
                        key={`mid-${i}`} x={mid.x - 4 / zoom} y={mid.y - 4 / zoom} width={8 / zoom} height={8 / zoom}
                        fill="#fff" stroke="#2563EB" strokeWidth={1.5 / zoom}
                        style={{ cursor: 'copy', pointerEvents: 'all' }}
                        onDoubleClick={(e) => { e.stopPropagation(); addVertexOnEdge(m, i) }}
                      />
                    )
                  }),
                  pts.map((v, vi) => {
                    const isSelectedVertex = selectedVertex?.measurementId === m.id && selectedVertex?.vertexIndex === vi
                    return (
                      <circle
                        key={vi} cx={v.x} cy={v.y} r={6 / zoom}
                        fill={isSelectedVertex ? '#FEE2E2' : '#fff'} stroke={isSelectedVertex ? '#DC2626' : '#F59E0B'} strokeWidth={2 / zoom}
                        style={{ cursor: 'grab', pointerEvents: 'all' }}
                        onMouseDown={(e) => handleVertexPointerDown(e, m, vi)}
                        onContextMenu={(e) => handleVertexContextMenu(e, m, vi)}
                      />
                    )
                  }),
                ]
              })}
            </svg>
          )}

          {/* PHASE 3 — live marquee rectangle. Window Select = solid
              blue outline (fully-contained semantics); Crossing Select
              = dashed green outline (intersects-or-contained
              semantics), matching common CAD convention so the user
              can tell which mode is active while dragging. Purely
              visual — pointerEvents:none so it never itself becomes a
              hit-test target. */}
          {marqueeStart && marqueeCurrent && (
            <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              <rect
                x={Math.min(marqueeStart.x, marqueeCurrent.x)} y={Math.min(marqueeStart.y, marqueeCurrent.y)}
                width={Math.abs(marqueeCurrent.x - marqueeStart.x)} height={Math.abs(marqueeCurrent.y - marqueeStart.y)}
                fill={tool === 'window-select' ? 'rgba(37,99,235,0.08)' : 'rgba(22,163,74,0.08)'}
                stroke={tool === 'window-select' ? '#2563EB' : '#16A34A'}
                strokeWidth={1.5 / zoom}
                strokeDasharray={tool === 'window-select' ? 'none' : `${5 / zoom} ${4 / zoom}`}
              />
            </svg>
          )}

          {/* Zoom Area — live preview rectangle. Purely visual; never
              creates a measurement, never touches selection state,
              never written to the database. */}
          {zoomAreaStart && zoomAreaCurrent && (
            <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              <rect
                x={Math.min(zoomAreaStart.x, zoomAreaCurrent.x)} y={Math.min(zoomAreaStart.y, zoomAreaCurrent.y)}
                width={Math.abs(zoomAreaCurrent.x - zoomAreaStart.x)} height={Math.abs(zoomAreaCurrent.y - zoomAreaStart.y)}
                fill="rgba(245,158,11,0.10)"
                stroke="#F59E0B"
                strokeWidth={1.5 / zoom}
                strokeDasharray={`${5 / zoom} ${4 / zoom}`}
              />
            </svg>
          )}

          {/* PHASE 4 — live lasso path. Same visual language as
              Crossing Select (dashed green) since Lasso behaves like a
              freeform Crossing selection. */}
          {lassoPoints && lassoPoints.length >= 2 && (
            <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              <polygon
                points={lassoPoints.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="rgba(22,163,74,0.08)" stroke="#16A34A" strokeWidth={1.5 / zoom}
                strokeDasharray={`${5 / zoom} ${4 / zoom}`}
              />
            </svg>
          )}
        </div>

        {livePreview && (
          <div style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(17,24,39,0.85)', color: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
            {livePreview.type === 'area' ? `Area: ${livePreview.value.toFixed(2)} m²` : `Length: ${livePreview.value.toFixed(2)} m`}
          </div>
        )}

        {/* Package 1b — minimal vertex context menu. Fixed-position
            HTML overlay in screen (client) coordinates, positioned at
            the right-click location — deliberately outside the PDF-
            space-transformed <svg> layers, since this is UI chrome,
            not drawing content. */}
        {vertexContextMenu && (
          <>
            <div
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
              onClick={() => setVertexContextMenu(null)}
              onContextMenu={(e) => { e.preventDefault(); setVertexContextMenu(null) }}
            />
            <div style={{
              position: 'fixed', top: vertexContextMenu.y, left: vertexContextMenu.x, zIndex: 1000,
              background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 140,
            }}>
              <button
                onClick={() => { setVertexContextMenu(null); const mm = existingMeasurements.find((x) => x.id === vertexContextMenu.measurementId); if (mm) removeVertex(mm, vertexContextMenu.vertexIndex) }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: colors.danger }}
              >
                🗑 Remove Vertex
              </button>
            </div>
          </>
        )}
      </div>

      <StatusBar cursorPdf={cursorPdf} zoom={zoom} calibration={calibration} scaleFactor={scaleFactor} tool={tool} pointCount={points.length} gridOn={gridOn} snapOn={snapOn} />
    </div>
  )
}

function GridLines({ width, height, spacing, zoom }) {
  // Dynamic grid: at low zoom, draw a coarser effective spacing so lines
  // don't merge into visual noise; the underlying snap grid stays fixed.
  const effectiveSpacing = zoom < 0.5 ? spacing * 4 : zoom < 1 ? spacing * 2 : spacing
  const lines = []
  for (let x = 0; x <= width; x += effectiveSpacing) lines.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={height} stroke="rgba(0,0,0,0.06)" strokeWidth={1 / zoom} />)
  for (let y = 0; y <= height; y += effectiveSpacing) lines.push(<line key={`h${y}`} x1={0} y1={y} x2={width} y2={y} stroke="rgba(0,0,0,0.06)" strokeWidth={1 / zoom} />)
  return <>{lines}</>
}

function SnapIndicator({ indicator, zoom }) {
  const { point, type } = indicator
  const r = 7 / zoom
  const sw = 2 / zoom
  if (type === 'endpoint') return <circle cx={point.x} cy={point.y} r={r} fill="none" stroke="#16A34A" strokeWidth={sw} />
  if (type === 'midpoint') return (
    <rect x={point.x - r} y={point.y - r} width={r * 2} height={r * 2} fill="none" stroke="#2563EB" strokeWidth={sw} transform={`rotate(45 ${point.x} ${point.y})`} />
  )
  if (type === 'intersection') return (
    <g stroke="#DC2626" strokeWidth={sw}>
      <line x1={point.x - r} y1={point.y} x2={point.x + r} y2={point.y} />
      <line x1={point.x} y1={point.y - r} x2={point.x} y2={point.y + r} />
    </g>
  )
  if (type === 'grid') return <circle cx={point.x} cy={point.y} r={r / 2} fill="#9CA3AF" />
  return null
}

// Returns the 4 corner points of a rectangle stored as a 2-point
// diagonal — the SAME reconstruction MeasurementGeometry already uses
// for rendering, reused here for hit-testing/area so both stay
// consistent with a single source of truth for "what a rectangle is".
// Package 1c — Rotation. Pure geometric primitive, rotates a point
// around a center by an angle in radians. Standard rotation matrix —
// an isometry, so distances between points (and therefore area/length
// computed from them) are mathematically preserved.
function rotatePoint(pt, center, angleRad) {
  const cos = Math.cos(angleRad), sin = Math.sin(angleRad)
  const dx = pt.x - center.x, dy = pt.y - center.y
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos }
}

function centroidOf(points) {
  const n = points.length
  return { x: points.reduce((s, p) => s + p.x, 0) / n, y: points.reduce((s, p) => s + p.y, 0) / n }
}

function rectangleCorners(points) {
  return [points[0], { x: points[1].x, y: points[0].y }, points[1], { x: points[0].x, y: points[1].y }]
}

// Standard ray-casting point-in-polygon test — used ONLY for selection
// priority, not for any quantity/business calculation.
function isPointInPolygon(pt, polyPts) {
  let inside = false
  for (let i = 0, j = polyPts.length - 1; i < polyPts.length; j = i++) {
    const xi = polyPts[i].x, yi = polyPts[i].y
    const xj = polyPts[j].x, yj = polyPts[j].y
    const intersect = ((yi > pt.y) !== (yj > pt.y)) && (pt.x < (xj - xi) * (pt.y - yi) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

// Shoelace formula — used ONLY to rank overlapping area candidates by
// size for selection priority (smallest-area-wins). This is NOT the
// Measurement Engine's calculated_value and never touches it.
function polygonAreaForSelection(polyPts) {
  let sum = 0
  for (let i = 0; i < polyPts.length; i++) {
    const j = (i + 1) % polyPts.length
    sum += polyPts[i].x * polyPts[j].y - polyPts[j].x * polyPts[i].y
  }
  return Math.abs(sum / 2)
}

function isPointNearGeometry(pt, g, radius) {
  if (!g) return false
  const pts = g.points || (g.point ? [g.point] : [])
  if (g.type === 'count') return Math.hypot(pts[0].x - pt.x, pts[0].y - pt.y) <= radius
  // Interior test (Point-in-Polygon) runs FIRST for area geometry — a
  // click clearly inside a polygon/rectangle must select it.
  const polyPts = g.type === 'rectangle' ? rectangleCorners(pts) : pts
  if (g.type === 'polygon' || g.type === 'rectangle') {
    if (isPointInPolygon(pt, polyPts)) return true
  }
  // Boundary fallback. FIX: rectangles are stored as 2 diagonal corner
  // points, so iterating `pts` directly here only ever tested distance
  // to a single diagonal segment between those two points — clicking
  // on any of the 4 real rectangle edges (other than that diagonal)
  // could silently miss. `edgePts` uses the same 4-corner
  // reconstruction as the interior test (and as MeasurementGeometry's
  // rendering) so all four real edges are tested, while polygon/
  // length/polyline/dimension behavior is completely unchanged (they
  // already pass through pts === polyPts / edgePts unmodified).
  const edgePts = g.type === 'rectangle' ? polyPts : pts
  for (let i = 0; i < edgePts.length; i++) {
    const a = edgePts[i]
    const b = edgePts[(i + 1) % edgePts.length]
    if (distToSegment(pt, a, b) <= radius) return true
    if (edgePts.length < 2 && Math.hypot(a.x - pt.x, a.y - pt.y) <= radius) return true
  }
  return false
}
function distToSegment(p, a, b) {
  const l2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2
  if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * (b.x - a.x)), p.y - (a.y + t * (b.y - a.y)))
}

// ==========================================================================
// PHASE 3 — Window Select / Crossing Select geometry predicates.
// These operate on the SAME PDF-space geometry (g.points / g.point) used
// throughout the file — no second coordinate system, no new business
// calculation. Marquee box is { x1, y1, x2, y2 } in PDF coordinates
// (already normalized so x1<=x2, y1<=y2 by the caller).
// ==========================================================================

function pointInBox(pt, box) {
  return pt.x >= box.x1 && pt.x <= box.x2 && pt.y >= box.y1 && pt.y <= box.y2
}

// Standard segment/segment intersection test (proper or touching), used
// for Crossing Select edge-vs-box-edge checks.
function segmentsIntersect(p1, p2, p3, p4) {
  const d = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x)
  if (d === 0) return false // parallel/collinear — box edges are axis-aligned so this is a rare exact case; containment/point checks elsewhere cover it
  const t = ((p3.x - p1.x) * (p4.y - p3.y) - (p3.y - p1.y) * (p4.x - p3.x)) / d
  const u = ((p3.x - p1.x) * (p2.y - p1.y) - (p3.y - p1.y) * (p2.x - p1.x)) / d
  return t >= 0 && t <= 1 && u >= 0 && u <= 1
}

function segmentIntersectsBox(a, b, box) {
  if (pointInBox(a, box) || pointInBox(b, box)) return true
  const corners = [
    { x: box.x1, y: box.y1 }, { x: box.x2, y: box.y1 },
    { x: box.x2, y: box.y2 }, { x: box.x1, y: box.y2 },
  ]
  for (let i = 0; i < 4; i++) {
    if (segmentsIntersect(a, b, corners[i], corners[(i + 1) % 4])) return true
  }
  return false
}

// Returns the geometry's point list in the SAME representation used
// everywhere else in this file (rectangle -> 4 real corners via the
// existing rectangleCorners() helper; polygon -> its own points as-is,
// which already supports concave shapes with no special-casing needed
// since containment/intersection here work edge-by-edge and point-by-
// point, not via convexity assumptions).
function geometryPointsFor(g) {
  if (g.type === 'rectangle') return rectangleCorners(g.points)
  if (g.type === 'polygon' || g.type === 'polyline' || g.type === 'length' || g.type === 'dimension') return g.points
  return null // 'count' handled separately (single point)
}

// Window Select: measurement geometry must be FULLY contained in box.
function isGeometryFullyContainedInBox(g, box) {
  if (g.type === 'count') return pointInBox(g.point, box)
  const pts = geometryPointsFor(g)
  if (!pts) return false
  return pts.every((p) => pointInBox(p, box))
}

// Crossing Select: measurement geometry fully contained OR intersecting
// the box boundary (standard CAD crossing-selection semantics).
function isGeometryIntersectingBox(g, box) {
  if (g.type === 'count') return pointInBox(g.point, box)
  const pts = geometryPointsFor(g)
  if (!pts) return false
  if (pts.some((p) => pointInBox(p, box))) return true // any vertex inside box
  // Also catch the case where the box is entirely inside a large
  // shape (no vertex inside the box) by testing the box's own corner
  // against the polygon interior (area types only; line types have no
  // interior to be "inside").
  if ((g.type === 'polygon' || g.type === 'rectangle')) {
    const boxCorner = { x: box.x1, y: box.y1 }
    if (isPointInPolygon(boxCorner, pts)) return true
  }
  // Edge-by-edge intersection against the box boundary — works
  // identically for convex and concave polygons since it never
  // assumes convexity, only tests each real edge in turn.
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    if (g.type !== 'polygon' && g.type !== 'rectangle' && i === pts.length - 1) break // open polyline/length/dimension: don't wrap last->first
    if (segmentIntersectsBox(a, b, box)) return true
  }
  return false
}

// ==========================================================================
// PHASE 4 — Lasso Select. Generalizes the box-based predicates above to
// an arbitrary closed polygon (the lasso path), reusing the SAME
// isPointInPolygon/segmentsIntersect primitives — no duplicated
// geometry-engine math. A box is just a 4-point axis-aligned polygon,
// so isGeometryFullyContainedInBox/isGeometryIntersectingBox are kept
// as-is for Window/Crossing (already tested, untouched), and these
// two new functions mirror their exact logic against a general polygon.
// ==========================================================================

function segmentIntersectsPolygon(a, b, lassoPts) {
  for (let i = 0; i < lassoPts.length; i++) {
    if (segmentsIntersect(a, b, lassoPts[i], lassoPts[(i + 1) % lassoPts.length])) return true
  }
  return false
}

function isGeometryFullyContainedInLasso(g, lassoPts) {
  if (g.type === 'count') return isPointInPolygon(g.point, lassoPts)
  const pts = geometryPointsFor(g)
  if (!pts) return false
  return pts.every((p) => isPointInPolygon(p, lassoPts))
}

function isGeometryIntersectingLasso(g, lassoPts) {
  if (g.type === 'count') return isPointInPolygon(g.point, lassoPts)
  const pts = geometryPointsFor(g)
  if (!pts) return false
  if (pts.some((p) => isPointInPolygon(p, lassoPts))) return true // any measurement vertex inside lasso
  if (g.type === 'polygon' || g.type === 'rectangle') {
    // Also catch the lasso being entirely inside a large measurement
    // (no measurement vertex inside the lasso) by testing one lasso
    // vertex against the measurement's own interior.
    if (isPointInPolygon(lassoPts[0], pts)) return true
  }
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    if (g.type !== 'polygon' && g.type !== 'rectangle' && i === pts.length - 1) break // open lines: don't wrap last->first
    if (segmentIntersectsPolygon(a, b, lassoPts)) return true
  }
  return false
}

// Light path simplification: drops consecutive points closer than
// `minDist` PDF units apart. This only removes redundant near-duplicate
// points accumulated from fast mousemove sampling — it does not alter
// the actual shape enough to change selection results, and keeps the
// lasso responsive even for long, fast drags.
function simplifyLassoPath(points, minDist = 2) {
  if (points.length <= 2) return points
  const out = [points[0]]
  for (let i = 1; i < points.length; i++) {
    const last = out[out.length - 1]
    if (Math.hypot(points[i].x - last.x, points[i].y - last.y) >= minDist) out.push(points[i])
  }
  return out
}

// False only for geometry that is declared as a drawable kind but is missing
// the data that kind needs: these are exactly the shapes MeasurementGeometry
// and the selection code would crash on. Anything else (including shapes with
// no type, manual entries, and unknown types) is left exactly as before.
function hasDrawableGeometry(m) {
  const g = m && m.geometry_data
  if (!g) return true // the existing `if (!g)` checks already handle a missing geometry
  switch (g.type) {
    case 'polygon':
    case 'polyline':
    case 'length':
    case 'dimension':
      return Array.isArray(g.points)
    case 'rectangle':
      return Array.isArray(g.points) && g.points.length >= 2
    case 'count':
      return !!g.point && typeof g.point.x === 'number' && typeof g.point.y === 'number'
    default:
      return true
  }
}

function MeasurementGeometry({ measurement, selected, isPrimary, hovered, zoom }) {
  const g = measurement.geometry_data
  if (!g) return null
  const isNegative = measurement.polarity === 'negative'
  // Primary gets a slightly stronger stroke than secondary selected
  // items — a subtle emphasis, not a redesign of the visual language.
  const strokeW = (selected ? (isPrimary ? 3.5 : 3) : hovered ? 2.5 : 2) / zoom
  // Package 2 — negative measurements get a distinct, muted color in
  // their normal (unselected/unhovered) state only; Selected/Hovered
  // states keep their existing colors unchanged so interaction
  // feedback remains fully consistent regardless of polarity. The
  // shape itself (points/dimensions) is completely untouched.
  // NOTE: '#DC2626' is already used by Count's default (unselected)
  // color below, and length/polyline/dimension already use their own
  // default '#EA580C' — a different color ('#7C3AED', violet) is used
  // here specifically to avoid colliding with any existing default
  // color across all geometry types.
  const color = selected ? '#F59E0B' : hovered ? '#2563EB' : isNegative ? '#7C3AED' : '#16A34A'
  const dashArray = (!selected && !hovered && isNegative) ? `${6 / zoom} ${3 / zoom}` : undefined

  if (g.type === 'polygon' || g.type === 'rectangle') {
    const pts = g.type === 'rectangle'
      ? [g.points[0], { x: g.points[1].x, y: g.points[0].y }, g.points[1], { x: g.points[0].x, y: g.points[1].y }]
      : g.points
    return <polygon points={pts.map((p) => `${p.x},${p.y}`).join(' ')} fill={selected ? 'rgba(245,158,11,0.18)' : hovered ? 'rgba(37,99,235,0.10)' : isNegative ? 'rgba(124,58,237,0.12)' : 'rgba(34,197,94,0.12)'} stroke={color} strokeWidth={strokeW} strokeDasharray={dashArray} />
  }
  if (g.type === 'length' || g.type === 'polyline' || g.type === 'dimension') {
    return <polyline points={g.points.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={selected ? color : hovered ? color : isNegative ? color : '#EA580C'} strokeWidth={strokeW} strokeDasharray={dashArray} />
  }
  if (g.type === 'count') {
    return (
      <g>
        <circle cx={g.point.x} cy={g.point.y} r={9 / zoom} fill={selected ? color : hovered ? color : isNegative ? color : '#DC2626'} />
        <text x={g.point.x} y={g.point.y} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={9 / zoom} fontWeight="bold">
          {measurement.displayIndex ?? ''}
        </text>
      </g>
    )
  }
  return null
}

function Toolbar({ tool, setTool, zoom, zoomBy, onFitWidth, onFitHeight, onFitDrawing, onActualSize, calibrated, onUndo, canUndo, onRedo, canRedo, onCancel, onDeleteSelected, hasSelection, gridOn, setGridOn, snapOn, setSnapOn, onDuplicate, onRotateLeft, onRotateRight, onMoveUp, onMoveDown, onMoveLeft, onMoveRight, onTogglePolarity, isNegative, isolateActive, onToggleIsolate, isLocked, onToggleLock, fullCanvasMode, onToggleFullCanvas, onSubmitReview, hasBatchSelection }) {
  const navTools = TOOLS.filter((t) => ['select', 'window-select', 'crossing-select', 'lasso-select'].includes(t.id))
  const panTools = TOOLS.filter((t) => t.id === 'pan' || t.id === 'zoom-area')
  const measureTools = TOOLS.filter((t) => ['calibrate', 'length', 'polyline', 'area', 'rectangle', 'count', 'dimension'].includes(t.id))
  return (
    // UX fix (layout redesign): CSS Grid with grid-auto-columns:
    // max-content instead of flex — this is the actual root cause fix
    // required. Flex + wrap was distributing leftover space AFTER a
    // wrap unpredictably (browser-decided), which is exactly what
    // produced the large uneven empty areas. Grid with max-content
    // columns gives each group EXACTLY the width its content needs,
    // no more — so groups sit flush against their separators with
    // zero uncontrolled slack, on both rows.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '6px 12px', minWidth: 0, overflowX: 'auto', background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
      {/* Row 1 — Selection, Navigation, Measurement */}
      <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'max-content', alignItems: 'start', gap: 8, justifyContent: 'start' }}>
        <ToolbarGroup label="SELECTION">
          {navTools.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} title={`${t.label} (${t.shortcut})`} style={proToolBtnStyle(tool === t.id, false)}>
              <span style={{ fontSize: 18 }}>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
        </ToolbarGroup>
        <ToolbarSeparator />

        <ToolbarGroup label="NAVIGATION">
          {panTools.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} title={t.shortcut ? `${t.label} (${t.shortcut})` : t.id === 'zoom-area' ? 'Drag a rectangle to zoom into that area' : t.label} style={proToolBtnStyle(tool === t.id, false)}>
              <span style={{ fontSize: 18 }}>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
          <button onClick={onFitWidth} title="Fit Width" style={proToolBtnStyle(false, false)}><span style={{ fontSize: 18 }}>⬌</span><span>Fit W</span></button>
          <button onClick={onFitHeight} title="Fit Height" style={proToolBtnStyle(false, false)}><span style={{ fontSize: 18 }}>⬍</span><span>Fit H</span></button>
          <button onClick={onFitDrawing} title="Fit Drawing" style={proToolBtnStyle(false, false)}><span style={{ fontSize: 18 }}>⛶</span><span>Fit</span></button>
          <button onClick={onActualSize} style={proToolBtnStyle(false, false)}><span style={{ fontSize: 18 }}>1:1</span><span>Actual</span></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 46 }}>
            <button onClick={() => zoomBy(-0.1)} style={{ ...iconBtnStyle, width: 26, height: 26 }}>−</button>
            <span style={{ fontSize: 11, color: colors.textSecondary, width: 36, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => zoomBy(0.1)} style={{ ...iconBtnStyle, width: 26, height: 26 }}>+</button>
          </div>
        </ToolbarGroup>
        <ToolbarSeparator />

        <ToolbarGroup label="MEASUREMENT">
          {measureTools.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} disabled={t.id !== 'calibrate' && !calibrated}
              title={`${t.label} (${t.shortcut})`} style={proToolBtnStyle(tool === t.id, t.id !== 'calibrate' && !calibrated)}>
              <span style={{ fontSize: 18 }}>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
        </ToolbarGroup>
      </div>

      {/* Row 2 — Editing (including Geometry Nudge), Visibility, Workspace.
          Same max-content Grid discipline: WORKSPACE sits flush after
          VISIBILITY's separator, not floating alone with empty space
          around it. Cancel (⊗) moved here from its old floating
          position — it's an editing-adjacent action (cancels the
          current in-progress drawing), so EDITING is its natural home
          rather than a standalone slot. */}
      <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'max-content', alignItems: 'start', gap: 8, justifyContent: 'start' }}>
        <ToolbarGroup label="EDITING">
          <button onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z / Backspace)" style={proToolBtnStyle(false, !canUndo)}><span style={{ fontSize: 18 }}>↶</span><span>Undo</span></button>
          <button onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)" style={proToolBtnStyle(false, !canRedo)}><span style={{ fontSize: 18 }}>↷</span><span>Redo</span></button>
          <button onClick={onCancel} title="Cancel (Esc)" style={proToolBtnStyle(false, false)}><span style={{ fontSize: 18 }}>⊗</span><span>Cancel</span></button>
          <button onClick={onDeleteSelected} disabled={!hasSelection} title="Delete selected" style={proToolBtnStyle(false, !hasSelection)}><span style={{ fontSize: 18 }}>🗑</span><span>Delete</span></button>
          {/* Package 4 — Submit for Review. Deliberately gated on
              hasBatchSelection (selectedMeasurementIds.length > 0), NOT
              hasSelection (the single-id flag Delete uses) — this is
              the first real batch-scoped action in this toolbar. */}
          <button onClick={onSubmitReview} disabled={!hasBatchSelection} title="Submit selected for review" style={proToolBtnStyle(false, !hasBatchSelection)}><span style={{ fontSize: 18 }}>📤</span><span>Submit Review</span></button>
          <button onClick={onDuplicate} disabled={!hasSelection} title="Duplicate selected measurement" style={proToolBtnStyle(false, !hasSelection)}><span style={{ fontSize: 18 }}>⧉</span><span>Dup</span></button>
          <button onClick={onRotateLeft} disabled={!hasSelection} title="Rotate Left 90°" style={proToolBtnStyle(false, !hasSelection)}><span style={{ fontSize: 18 }}>↺</span><span>Rot L</span></button>
          <button onClick={onRotateRight} disabled={!hasSelection} title="Rotate Right 90°" style={proToolBtnStyle(false, !hasSelection)}><span style={{ fontSize: 18 }}>↻</span><span>Rot R</span></button>
          <button onClick={onTogglePolarity} disabled={!hasSelection} title={isNegative ? 'Mark as Positive' : 'Mark as Negative (deduction)'} style={proToolBtnStyle(isNegative, !hasSelection)}>
            <span style={{ fontSize: 18 }}>{isNegative ? '➖' : '➕'}</span><span>{isNegative ? 'Neg' : 'Pos'}</span>
          </button>
          {/* Geometry Nudge — moves the SELECTED MEASUREMENT geometry
              (not the Canvas viewport; that is Pan, in NAVIGATION).
              Same moveGeometry()/commitGeometryEdit() pipeline as
              before — Lock is enforced inside commitGeometryEdit
              itself, unchanged. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 24px)', gridTemplateRows: 'repeat(2, 22px)', gap: 1 }}>
            <div />
            <button onClick={onMoveUp} disabled={!hasSelection} title="Move selected geometry up" style={{ ...iconBtnStyle, width: 24, height: 22, padding: 0, fontSize: 11, opacity: hasSelection ? 1 : 0.4, cursor: hasSelection ? 'pointer' : 'not-allowed' }}>↑</button>
            <div />
            <button onClick={onMoveLeft} disabled={!hasSelection} title="Move selected geometry left" style={{ ...iconBtnStyle, width: 24, height: 22, padding: 0, fontSize: 11, opacity: hasSelection ? 1 : 0.4, cursor: hasSelection ? 'pointer' : 'not-allowed' }}>←</button>
            <button onClick={onMoveDown} disabled={!hasSelection} title="Move selected geometry down" style={{ ...iconBtnStyle, width: 24, height: 22, padding: 0, fontSize: 11, opacity: hasSelection ? 1 : 0.4, cursor: hasSelection ? 'pointer' : 'not-allowed' }}>↓</button>
            <button onClick={onMoveRight} disabled={!hasSelection} title="Move selected geometry right" style={{ ...iconBtnStyle, width: 24, height: 22, padding: 0, fontSize: 11, opacity: hasSelection ? 1 : 0.4, cursor: hasSelection ? 'pointer' : 'not-allowed' }}>→</button>
          </div>
        </ToolbarGroup>
        <ToolbarSeparator />

        {/* VISIBILITY group — Invert removed entirely (Package 3 UX
            decision): the Canvas operates on one Active Takeoff Item
            at a time, so reversing visibility across measurement types
            provided no meaningful workflow value. */}
        <ToolbarGroup label="VISIBILITY">
          <button onClick={onToggleIsolate} disabled={!hasSelection && !isolateActive} title={isolateActive ? 'Exit Isolate' : 'Isolate selected measurement'} style={proToolBtnStyle(isolateActive, !hasSelection && !isolateActive)}>
            <span style={{ fontSize: 18 }}>◎</span><span>Isolate</span>
          </button>
          <button onClick={onToggleLock} disabled={!hasSelection} title={isLocked ? 'Unlock selected measurement' : 'Lock selected measurement'} style={proToolBtnStyle(isLocked, !hasSelection)}>
            <span style={{ fontSize: 18 }}>{isLocked ? '🔒' : '🔓'}</span><span>{isLocked ? 'Locked' : 'Lock'}</span>
          </button>
          <button onClick={() => setGridOn((g) => !g)} title="Toggle Grid" style={proToolBtnStyle(gridOn, false)}><span style={{ fontSize: 18 }}>▦</span><span>Grid</span></button>
          <button onClick={() => setSnapOn((s) => !s)} title="Toggle Snap" style={proToolBtnStyle(snapOn, false)}><span style={{ fontSize: 18 }}>🧲</span><span>Snap</span></button>
        </ToolbarGroup>
        <ToolbarSeparator />

        <ToolbarGroup label="WORKSPACE">
          <button onClick={onToggleFullCanvas} title={fullCanvasMode ? 'Exit Full Canvas' : 'Full Canvas (maximize workspace)'} style={proToolBtnStyle(fullCanvasMode, false)}>
            <span style={{ fontSize: 18 }}>⛶</span><span>{fullCanvasMode ? 'Exit Full' : 'Full Canvas'}</span>
          </button>
        </ToolbarGroup>
      </div>
    </div>
  )
}

// UX refinement — subtle, professional group label above each Toolbar
// group. The label itself stays visually quiet (small, muted color)
// so the actual tool buttons remain the prominent element, exactly as
// required ("group labels subtle, tool buttons remain prominent").
function ToolbarGroup({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', color: colors.textMuted, textTransform: 'uppercase', paddingLeft: 2 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>{children}</div>
    </div>
  )
}

function ToolbarSeparator() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: colors.border, margin: '0 4px' }} />
}

function StatusBar({ cursorPdf, zoom, calibration, scaleFactor, tool, pointCount, gridOn, snapOn }) {
  return (
    <div style={{ display: 'flex', gap: 20, padding: '6px 14px', background: '#111827', color: '#D1D5DB', fontSize: 12, fontFamily: 'monospace' }}>
      <span>X: {cursorPdf ? cursorPdf.x.toFixed(1) : '—'}</span>
      <span>Y: {cursorPdf ? cursorPdf.y.toFixed(1) : '—'}</span>
      <span>Zoom: {Math.round(zoom * 100)}%</span>
      <span style={{ color: calibration ? '#4ADE80' : '#FBBF24' }}>
        {calibration ? `Scale: 1 PDF unit = ${scaleFactor.toFixed(4)} ${calibration.unit}` : 'Not calibrated'}
      </span>
      <span>Tool: {tool}{pointCount > 0 ? ` (${pointCount} pt)` : ''}</span>
      <span style={{ color: snapOn ? '#4ADE80' : '#6B7280' }}>Snap: {snapOn ? 'On' : 'Off'}</span>
      <span style={{ color: gridOn ? '#4ADE80' : '#6B7280' }}>Grid: {gridOn ? 'On' : 'Off'}</span>
    </div>
  )
}

// Package 3+8 — professional CAD-style button: ~44px min-height,
// larger icon (18px) with a label beneath, generous click target,
// clear active/disabled states. Existing iconBtnStyle/toolBtnStyle are
// preserved unchanged for compatibility (not removed), but the
// Toolbar above now uses this style exclusively for a denser,
// professional look without a second architecture — same colors
// object, same active/disabled convention as the original toolBtnStyle.
function proToolBtnStyle(active, disabled) {
  return {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
    minWidth: 52, height: 46, padding: '4px 6px', borderRadius: 8,
    border: `1px solid ${active ? colors.primary : colors.border}`,
    background: active ? '#EFF4FC' : '#fff', color: active ? colors.primary : colors.textPrimary,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1, fontSize: 10, fontWeight: 500,
  }
}

// Package 9-10 fix — computes the bounding box of the SUPPORTED
// entity list DxfLayer already knows how to render (LINE/LWPOLYLINE/
// POLYLINE via .points, CIRCLE/ARC via .center+.radius) — this is the
// SAME entity shape DxfLayer consumes, so no separate geometry
// interpretation is introduced. Runs once per DXF load, not on every
// render (called only from inside the Worker onmessage handler).
function computeDxfBoundingBox(entities) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const e of entities) {
    if (e.points) {
      for (const p of e.points) {
        if (p.x < minX) minX = p.x
        if (p.x > maxX) maxX = p.x
        if (p.y < minY) minY = p.y
        if (p.y > maxY) maxY = p.y
      }
    } else if (e.center) {
      if (e.center.x - e.radius < minX) minX = e.center.x - e.radius
      if (e.center.x + e.radius > maxX) maxX = e.center.x + e.radius
      if (e.center.y - e.radius < minY) minY = e.center.y - e.radius
      if (e.center.y + e.radius > maxY) maxY = e.center.y + e.radius
    }
  }
  if (!isFinite(minX)) return null // no supported entities at all — nothing to fit.
  return { minX, maxX, minY, maxY }
}

function toolBtnStyle(active, disabled) {
  return {
    padding: '6px 9px', fontSize: 12, borderRadius: 6, border: `1px solid ${active ? colors.primary : colors.border}`,
    background: active ? '#EFF4FC' : '#fff', color: active ? colors.primary : colors.textPrimary,
    cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, opacity: disabled ? 0.4 : 1,
  }
}
const iconBtnStyle = { width: 28, height: 28, borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', cursor: 'pointer', fontSize: 14 }
